<template>
  <div
    class="w-full lg:h-[calc(100vh-6rem)] lg:overflow-hidden mt-6 bg-gray-50 dark:bg-background rounded-t-[20px] flex flex-col lg:flex-row lg:items-stretch items-start px-4 md:px-8 gap-6 lg:gap-8"
  >
    <div class="flex flex-col justify-start lg:h-full gap-4">
      <div class="w-full md:w-32 flex-shrink-0">
        <BackButton />
      </div>
      <!-- Edit Learning Goal Sidebar -->
      <transition name="drawer-slide">
        <div
          v-if="showEditDrawer"
          class="fixed inset-0 z-[60] lg:sticky lg:top-20 lg:inset-auto lg:z-0 lg:block flex justify-start lg:justify-start"
        >
          <!-- Backdrop for mobile -->
          <div
            class="absolute inset-0 bg-black/40 backdrop-blur-sm lg:hidden h-screen"
            @click="closeEditDrawer"
          ></div>

          <div
            class="relative w-[85%] sm:w-[400px] lg:w-[360px] flex-shrink-0 lg:rounded-[20px] border-r lg:border border-border h-auto max-h-screen lg:max-h-[calc(100vh-8rem)] bg-card dark:bg-card mt-0 lg:ml-1 p-6 overflow-y-auto"
          >
            <div class="sticky top-6">
              <div class="flex items-center justify-between mb-8">
                <h2 class="text-[22px] font-semibold text-foreground">
                  Edit Learning Goal
                </h2>
                <button
                  @click="closeEditDrawer"
                  class="text-muted-foreground hover:text-foreground transition-colors p-1"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>

              <form
                @submit.prevent="submitEditGoal"
                class="flex flex-col h-full relative"
              >
                <div
                  class="space-y-5 flex-1 overflow-y-auto px-1 transition-all duration-300 pb-6"
                >
                  <!-- Topic Display -->
                  <div
                    class="space-y-1 p-2 rounded-[20px] transition-all cursor-pointer"
                    :class="
                      editingField === 'topic'
                        ? 'bg-background shadow-sm border border-border dark:bg-muted dark:border-border'
                        : 'hover:bg-muted/30 border border-transparent dark:hover:bg-muted/50'
                    "
                    @click="setEditingField('topic')"
                  >
                    <label
                      class="text-[14px] text-muted-foreground dark:text-gray-300 font-normal block"
                    >
                      What do you want to learn today?
                    </label>
                    <div class="text-[18px] font-bold text-foreground">
                      {{ editForm.topic || "Select a topic" }}
                    </div>

                    <!-- Inline Topic Edit -->
                    <transition name="expand">
                      <div
                        v-if="editingField === 'topic'"
                        class="mt-4 pt-2 border-t border-border/50"
                      >
                        <div
                          class="flex items-center gap-2 bg-muted/30 p-2 rounded-full border border-border"
                        >
                          <input
                            v-model="editForm.topic"
                            type="text"
                            class="flex-1 bg-transparent border-none outline-none text-[15px] text-foreground px-2"
                            placeholder="Type a topic..."
                            @keydown.enter.prevent="closeField"
                          />
                          <div class="flex gap-1">
                            <button
                              type="button"
                              @click.stop="editingField = null"
                              class="w-8 h-8 flex items-center justify-center bg-muted rounded-full hover:bg-muted/80 text-muted-foreground"
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              >
                                <line x1="19" y1="12" x2="5" y2="12"></line>
                                <polyline points="12 19 5 12 12 5"></polyline>
                              </svg>
                            </button>
                            <button
                              type="button"
                              @click.stop="closeField"
                              class="w-8 h-8 flex items-center justify-center bg-black dark:bg-white text-white dark:text-black rounded-full hover:opacity-80"
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              >
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </transition>
                  </div>

                  <!-- Experience Display -->
                  <div
                    class="space-y-1 p-2 rounded-[20px] transition-all cursor-pointer"
                    :class="
                      editingField === 'experience'
                        ? 'bg-background shadow-sm border border-border dark:bg-muted dark:border-border'
                        : 'hover:bg-muted/30 border border-transparent dark:hover:bg-muted/50'
                    "
                    @click="setEditingField('experience')"
                  >
                    <label
                      class="text-[14px] text-muted-foreground dark:text-gray-300 font-normal block"
                    >
                      What's your experience level?
                    </label>
                    <div class="text-[18px] font-bold text-foreground">
                      {{ editForm.experience || "Select level" }}
                    </div>

                    <!-- Inline Experience Edit -->
                    <transition name="expand">
                      <div
                        v-if="editingField === 'experience'"
                        class="mt-4 pt-2 border-t border-border/50"
                      >
                        <div class="flex flex-wrap gap-2 mb-3">
                          <button
                            v-for="level in [
                              'Beginner',
                              'Intermediate',
                              'Advanced',
                            ]"
                            :key="level"
                            type="button"
                            @click.stop="editForm.experience = level"
                            class="px-4 py-1.5 rounded-full text-[13px] font-medium border transition-colors"
                            :class="
                              editForm.experience === level
                                ? 'bg-black text-white border-black dark:bg-white dark:text-black'
                                : 'bg-card text-muted-foreground border-border'
                            "
                          >
                            {{ level }}
                          </button>
                        </div>
                        <div
                          class="flex items-center gap-2 bg-muted/30 p-2 rounded-full border border-border"
                        >
                          <input
                            v-model="editForm.experience"
                            type="text"
                            class="flex-1 bg-transparent border-none outline-none text-[15px] text-foreground px-2"
                            placeholder="Level..."
                          />
                          <button
                            type="button"
                            @click.stop="closeField"
                            class="w-8 h-8 flex items-center justify-center bg-black dark:bg-white text-white dark:text-black rounded-full hover:opacity-80"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </transition>
                  </div>

                  <!-- Time Display -->
                  <div
                    class="space-y-1 p-2 rounded-[20px] transition-all cursor-pointer"
                    :class="
                      editingField === 'timeCommitment'
                        ? 'bg-background shadow-sm border border-border dark:bg-muted dark:border-border'
                        : 'hover:bg-muted/30 border border-transparent dark:hover:bg-muted/50'
                    "
                    @click="setEditingField('timeCommitment')"
                  >
                    <label
                      class="text-[14px] text-muted-foreground dark:text-gray-300 font-normal block"
                    >
                      How much time do you have to learn?
                    </label>
                    <div class="text-[18px] font-bold text-foreground">
                      {{ editForm.timeCommitment || "Any" }}
                    </div>

                    <!-- Inline Time Edit -->
                    <transition name="expand">
                      <div
                        v-if="editingField === 'timeCommitment'"
                        class="mt-4 pt-2 border-t border-border/50"
                      >
                        <div class="flex flex-wrap gap-2 mb-3">
                          <button
                            v-for="option in durationOptions"
                            :key="option.title"
                            type="button"
                            @click.stop="selectDuration(option.value)"
                            class="px-4 py-1.5 rounded-full text-[13px] font-medium border transition-colors"
                            :class="
                              editForm.timeCommitment &&
                              editForm.timeCommitment.includes(option.value)
                                ? 'bg-black text-white border-black dark:bg-white dark:text-black'
                                : 'bg-card text-muted-foreground border-border'
                            "
                          >
                            {{ option.title }}
                          </button>
                        </div>
                        <div
                          class="flex items-center gap-2 bg-muted/30 p-2 rounded-full border border-border"
                        >
                          <input
                            v-model="editForm.timeCommitment"
                            type="text"
                            class="flex-1 bg-transparent border-none outline-none text-[15px] text-foreground px-2"
                            placeholder="Duration..."
                          />
                          <button
                            type="button"
                            @click.stop="closeField"
                            class="w-8 h-8 flex items-center justify-center bg-black dark:bg-white text-white dark:text-black rounded-full hover:opacity-80"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </transition>
                  </div>

                  <!-- Learning Style Display -->
                  <div
                    class="space-y-1 p-2 rounded-[20px] transition-all cursor-pointer"
                    :class="
                      editingField === 'learning_style'
                        ? 'bg-background shadow-sm border border-border dark:bg-muted dark:border-border'
                        : 'hover:bg-muted/30 border border-transparent dark:hover:bg-muted/50'
                    "
                    @click="setEditingField('learning_style')"
                  >
                    <label
                      class="text-[14px] text-muted-foreground dark:text-gray-300 font-normal block"
                    >
                      Preferred Learning Style
                    </label>
                    <div class="text-[18px] font-bold text-foreground">
                      {{ getLearningStyleTitle(editForm.learning_style) }}
                    </div>

                    <!-- Inline Learning Style Edit -->
                    <transition name="expand">
                      <div
                        v-if="editingField === 'learning_style'"
                        class="mt-4 pt-2 border-t border-border/50"
                      >
                        <div class="grid grid-cols-2 gap-2 mb-3">
                          <button
                            v-for="style in learningStyles"
                            :key="style.value"
                            type="button"
                            @click.stop="editForm.learning_style = style.value"
                            class="px-4 py-2 rounded-xl text-[12px] font-medium border transition-all text-left flex flex-col gap-1"
                            :class="
                              editForm.learning_style === style.value
                                ? 'bg-black text-white border-black dark:bg-white dark:text-black'
                                : 'bg-card text-muted-foreground border-border hover:bg-muted'
                            "
                          >
                            <span class="font-bold">{{ style.title }}</span>
                          </button>
                        </div>
                        <div class="flex items-center justify-end">
                          <button
                            type="button"
                            @click.stop="closeField"
                            class="w-8 h-8 flex items-center justify-center bg-black dark:bg-white text-white dark:text-black rounded-full hover:opacity-80"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </transition>
                  </div>

                  <!-- Mentor Display -->
                  <div
                    class="space-y-1 p-2 rounded-[20px] transition-all cursor-pointer"
                    :class="
                      editingField === 'mentor'
                        ? 'bg-background shadow-sm border border-border dark:bg-muted dark:border-border'
                        : 'hover:bg-muted/30 border border-transparent dark:hover:bg-muted/50'
                    "
                    @click="setEditingField('mentor')"
                  >
                    <label
                      class="text-[14px] text-muted-foreground dark:text-gray-300 font-normal block"
                    >
                      Do you want a human mentor?
                    </label>
                    <div class="text-[18px] font-bold text-foreground">
                      {{
                        editForm.mentor
                          ? selectedMentorDetails?.first_name
                            ? `${selectedMentorDetails.first_name} (Mentor)`
                            : "Yes"
                          : "Not yet"
                      }}
                    </div>

                    <!-- Inline Mentor Edit -->
                    <transition name="expand">
                      <div
                        v-if="editingField === 'mentor'"
                        class="mt-4 pt-2 border-t border-border/50"
                      >
                        <div class="flex items-center gap-2 mb-4">
                          <button
                            type="button"
                            @click.stop="editForm.mentor = false"
                            class="px-5 py-2 rounded-full text-[13px] font-medium border transition-all"
                            :class="
                              editForm.mentor === false
                                ? 'bg-black text-white'
                                : 'bg-card text-muted-foreground'
                            "
                          >
                            Not yet
                          </button>
                          <button
                            type="button"
                            @click.stop="editForm.mentor = true"
                            class="px-5 py-2 rounded-full text-[13px] font-medium border transition-all"
                            :class="
                              editForm.mentor === true
                                ? 'bg-black text-white'
                                : 'bg-card text-muted-foreground'
                            "
                          >
                            Yes, choose
                          </button>
                        </div>
                        <div
                          v-if="editForm.mentor"
                          class="flex items-center gap-2 bg-muted/30 p-2 rounded-full border border-border mb-4"
                        >
                          <input
                            v-model="mentorSearchQuery"
                            type="text"
                            class="flex-1 bg-transparent border-none outline-none text-[15px] text-foreground px-2"
                            placeholder="Search mentor..."
                            @input="handleMentorSearch"
                          />
                          <button
                            type="button"
                            @click.stop="closeField"
                            class="w-8 h-8 flex items-center justify-center bg-black dark:bg-white text-white dark:text-black rounded-full hover:opacity-80"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </transition>
                  </div>

                  <!-- Workspace Display -->
                  <div
                    class="space-y-1 p-2 rounded-[20px] transition-all cursor-pointer"
                    :class="
                      editingField === 'workspace'
                        ? 'bg-background shadow-sm border border-border dark:bg-muted dark:border-border'
                        : 'hover:bg-muted/30 border border-transparent dark:hover:bg-muted/50'
                    "
                    @click="setEditingField('workspace')"
                  >
                    <label
                      class="text-[14px] text-muted-foreground dark:text-gray-300 font-normal block"
                    >
                      Physical Workspace
                    </label>
                    <div class="text-[18px] font-bold text-foreground">
                      {{ selectedWorkspaceData?.name || "Not yet" }}
                    </div>

                    <!-- Inline Workspace Edit -->
                    <transition name="expand">
                      <div
                        v-if="editingField === 'workspace'"
                        class="mt-4 pt-2 border-t border-border/50"
                      >
                        <div class="flex items-center gap-2 mb-4">
                          <button
                            type="button"
                            @click.stop="selectWorkspaceOption('not_yet')"
                            class="px-5 py-2 rounded-full text-[13px] font-medium border transition-all"
                            :class="
                              workspaceOption === 'not_yet'
                                ? 'bg-black text-white'
                                : 'bg-card text-muted-foreground'
                            "
                          >
                            Not yet
                          </button>
                          <button
                            type="button"
                            @click.stop="selectWorkspaceOption('yes')"
                            class="px-5 py-2 rounded-full text-[13px] font-medium border transition-all"
                            :class="
                              workspaceOption === 'yes'
                                ? 'bg-black text-white'
                                : 'bg-card text-muted-foreground'
                            "
                          >
                            Yes, choose
                          </button>
                        </div>
                        <div
                          v-if="workspaceOption === 'yes'"
                          class="flex items-center gap-2 bg-muted/30 p-2 rounded-full border border-border"
                        >
                          <input
                            v-model="workspaceSearchQuery"
                            type="text"
                            class="flex-1 bg-transparent border-none outline-none text-[15px] text-foreground px-2"
                            placeholder="Search workspace..."
                            @input="handleWorkspaceSearch"
                          />
                          <button
                            type="button"
                            @click.stop="closeField"
                            class="w-8 h-8 flex items-center justify-center bg-black dark:bg-white text-white dark:text-black rounded-full hover:opacity-80"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </transition>
                  </div>

                  <!-- Resources Display -->
                  <div
                    class="space-y-1 p-2 rounded-[20px] transition-all cursor-pointer"
                    :class="
                      editingField === 'resourceOrigin'
                        ? 'bg-background shadow-sm border border-border dark:bg-muted dark:border-border'
                        : 'hover:bg-muted/30 border border-transparent dark:hover:bg-muted/50'
                    "
                    @click="setEditingField('resourceOrigin')"
                  >
                    <label
                      class="text-[14px] text-muted-foreground dark:text-gray-300 font-normal block"
                    >
                      Learning Resources
                    </label>
                    <div class="space-y-1">
                      <div
                        class="text-[18px] font-bold text-foreground capitalize"
                      >
                        {{
                          editForm.resourceOrigin === "online"
                            ? "Free (Online)"
                            : editForm.resourceOrigin === "verified"
                            ? "High-Quality"
                            : "Personal"
                        }}
                      </div>
                      <div
                        v-if="
                          editForm.resourceOrigin === 'personal' &&
                          editForm.personal_resources.length > 0
                        "
                        class="space-y-1 mt-2"
                      >
                        <a
                          v-for="res in editForm.personal_resources"
                          :key="res.id"
                          :href="res.url"
                          target="_blank"
                          @click.stop
                          class="flex items-center gap-2 text-[14px] text-blue-500 hover:underline"
                        >
                          <Paperclip class="w-3.5 h-3.5" />
                          <span class="truncate max-w-[200px]">{{
                            res.filename
                          }}</span>
                        </a>
                      </div>
                    </div>

                    <!-- Inline Resource Edit -->
                    <transition name="expand">
                      <div
                        v-if="editingField === 'resourceOrigin'"
                        class="mt-4 pt-2 border-t border-border/50 space-y-4"
                      >
                        <div class="flex flex-wrap gap-2">
                          <button
                            v-for="option in resourceOptions"
                            :key="option.value"
                            type="button"
                            @click.stop="editForm.resourceOrigin = option.value"
                            :disabled="
                              option.value === 'verified' && !isPaidUser
                            "
                            class="px-4 py-1.5 rounded-full text-[12px] font-medium border transition-all"
                            :class="
                              editForm.resourceOrigin === option.value
                                ? 'bg-black text-white'
                                : 'bg-card text-muted-foreground opacity-50'
                            "
                          >
                            {{ option.title }}
                          </button>
                        </div>
                        <div
                          class="bg-muted/30 p-3 rounded-xl text-[12px] text-muted-foreground border border-border/50"
                        >
                          {{
                            resourceOptions.find(
                              (o) => o.value === editForm.resourceOrigin,
                            )?.description
                          }}
                        </div>
                        <div
                          v-if="editForm.resourceOrigin === 'personal'"
                          class="space-y-3"
                        >
                          <div class="flex flex-wrap gap-2">
                            <div
                              v-for="(file, index) in resourceFiles"
                              :key="file.id"
                              class="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted border border-border text-[11px]"
                            >
                              <span class="truncate max-w-[100px]">{{
                                file.name
                              }}</span>
                              <button
                                type="button"
                                @click.stop="removeResourceFile(index)"
                                class="text-red-500"
                              >
                                ×
                              </button>
                            </div>
                            <div class="relative">
                              <button
                                type="button"
                                class="w-8 h-8 rounded-full border border-dashed border-border flex items-center justify-center text-muted-foreground"
                              >
                                +
                              </button>
                              <input
                                type="file"
                                multiple
                                class="absolute inset-0 opacity-0 cursor-pointer"
                                @change="handleResourceFileUpload"
                              />
                            </div>
                          </div>
                        </div>
                        <div class="flex justify-end">
                          <button
                            type="button"
                            @click.stop="closeField"
                            class="px-6 py-2 bg-black dark:bg-white text-white dark:text-black rounded-full text-[13px] font-bold shadow-sm"
                          >
                            Done
                          </button>
                        </div>
                      </div>
                    </transition>
                  </div>

                  <!-- Additional Info Display -->
                  <div
                    class="space-y-1 p-2 rounded-[20px] transition-all cursor-pointer"
                    :class="
                      editingField === 'additional_information'
                        ? 'bg-background shadow-sm border border-border dark:bg-muted dark:border-border'
                        : 'hover:bg-muted/30 border border-transparent dark:hover:bg-muted/50'
                    "
                    @click="setEditingField('additional_information')"
                  >
                    <label
                      class="text-[14px] text-muted-foreground dark:text-gray-300 font-normal block"
                    >
                      Additional Information
                    </label>
                    <div
                      class="text-[16px] font-medium text-foreground truncate"
                    >
                      {{ editForm.additional_information || "None provided" }}
                    </div>

                    <!-- Inline Additional Info Edit -->
                    <transition name="expand">
                      <div
                        v-if="editingField === 'additional_information'"
                        class="mt-4 pt-2 border-t border-border/50"
                      >
                        <textarea
                          v-model="editForm.additional_information"
                          class="w-full bg-muted/20 p-3 rounded-xl border border-border text-[15px] min-h-[80px] resize-none mb-3"
                          placeholder="Additional details..."
                        ></textarea>
                        <div class="flex justify-end">
                          <button
                            type="button"
                            @click.stop="saveField"
                            class="px-6 py-2 bg-black dark:bg-white text-white dark:text-black rounded-full text-[13px] font-bold shadow-sm"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    </transition>
                  </div>

                  <!-- Mentor Search Results (Inline) -->
                  <div
                    v-if="editingField === 'mentor' && editForm.mentor"
                    class="mt-2 space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar"
                  >
                    <div v-if="loadingMentors" class="flex justify-center py-4">
                      <Loader />
                    </div>
                    <div
                      v-else-if="mentors.length === 0"
                      class="text-center py-6 px-4 bg-muted/20 border border-dashed border-border rounded-xl"
                    >
                      <div
                        class="flex justify-center mb-2 text-muted-foreground/30"
                      >
                        <svg
                          width="32"
                          height="32"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M17 21C17 19.4076 17 18.6114 16.7323 17.9858C16.4251 17.2681 15.8569 16.6999 15.1392 16.3927C14.5136 16.125 13.7174 16.125 12.125 16.125H11.875C10.2826 16.125 9.48637 16.125 8.86082 16.3927C8.14312 16.6999 7.57488 17.2681 7.26773 17.9858C7 18.6114 7 19.4076 7 21"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                          />
                          <path
                            d="M15 7C15 8.65685 13.6569 10 12 10C10.3431 10 9 8.65685 9 7C9 5.34315 10.3431 4 12 4C13.6569 4 15 5.34315 15 7Z"
                            stroke="currentColor"
                            stroke-width="1.5"
                          />
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-dasharray="2 4"
                          />
                        </svg>
                      </div>
                      <p class="text-[13px] font-medium text-foreground">
                        No mentors found
                      </p>
                      <p class="text-[11px] text-muted-foreground mt-1">
                        {{
                          mentorSearchQuery
                            ? `We couldn't find any mentors matching "${mentorSearchQuery}".`
                            : "There are currently no mentors available matching your criteria."
                        }}
                      </p>
                    </div>
                    <div
                      v-else
                      class="max-h-[200px] overflow-y-auto pr-2 custom-scrollbar space-y-2"
                    >
                      <div
                        v-for="mentor in mentors"
                        :key="mentor.talent_id"
                        @click="selectMentor(mentor.talent_id)"
                        class="p-3 rounded-[12px] border cursor-pointer transition-all flex items-center gap-3"
                        :class="[
                          selectedMentorId === mentor.talent_id
                            ? 'border-primary bg-primary/5 dark:bg-primary/10'
                            : 'border-border bg-card hover:border-gray-300 dark:hover:border-gray-700',
                        ]"
                      >
                        <div
                          class="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-[14px] font-semibold text-foreground overflow-hidden"
                        >
                          <img
                            v-if="mentor.profile_picture"
                            :src="mentor.profile_picture"
                            alt="Mentor avatar"
                            class="w-full h-full object-cover"
                          />
                          <span v-else>{{
                            mentor.first_name?.[0]?.toUpperCase() || "U"
                          }}</span>
                        </div>
                        <div class="flex-1 min-w-0">
                          <p
                            class="text-[14px] font-semibold text-foreground truncate"
                          >
                            {{ mentor.first_name }} {{ mentor.last_name }}
                          </p>
                          <p class="text-[12px] text-muted-foreground truncate">
                            {{
                              mentor.skills?.[0] ||
                              mentor.expert_achievements?.[0]?.name ||
                              "Expert Mentor"
                            }}
                          </p>
                        </div>
                        <div
                          v-if="selectedMentorId === mentor.talent_id"
                          class="text-primary"
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M16.6666 5L7.49992 14.1667L3.33325 10"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Workspace Search Results (Inline) -->
                  <div
                    v-if="
                      editingField === 'workspace' && workspaceOption === 'yes'
                    "
                    class="mt-2 space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar"
                  >
                    <div
                      v-if="loadingWorkspaces"
                      class="flex justify-center py-4"
                    >
                      <Loader />
                    </div>
                    <div
                      v-else-if="workspaces.length === 0"
                      class="text-center py-6 px-4 bg-muted/20 border border-dashed border-border rounded-xl"
                    >
                      <p class="text-[13px] font-medium text-foreground">
                        No workspaces found
                      </p>
                      <p class="text-[11px] text-muted-foreground mt-1">
                        {{
                          workspaceSearchQuery
                            ? `We couldn't find any workspaces matching "${workspaceSearchQuery}".`
                            : "There are currently no workspaces available matching your criteria."
                        }}
                      </p>
                    </div>
                    <div v-else class="space-y-4">
                      <div
                        v-for="workspace in workspaces"
                        :key="workspace.id"
                        class="p-4 rounded-[16px] border transition-all"
                        :class="[
                          editForm.workspace_id === workspace.id
                            ? 'border-primary bg-primary/5'
                            : 'border-border bg-card',
                        ]"
                      >
                        <div
                          class="flex items-start justify-between cursor-pointer"
                          @click="toggleWorkspaceExpansion(workspace.id)"
                        >
                          <div class="flex-1 min-w-0">
                            <h4 class="text-[15px] font-bold text-foreground">
                              {{ workspace.name }}
                            </h4>
                            <p
                              class="text-[12px] text-muted-foreground truncate"
                            >
                              {{ workspace.address }}
                            </p>
                          </div>
                          <div class="flex items-center gap-2">
                            <button
                              type="button"
                              @click.stop="selectWorkspace(workspace.id)"
                              class="px-3 py-1 rounded-full text-[12px] font-bold transition-all"
                              :class="[
                                editForm.workspace_id === workspace.id
                                  ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white'
                                  : 'bg-muted text-foreground hover:bg-muted/80',
                              ]"
                            >
                              {{
                                editForm.workspace_id === workspace.id
                                  ? "Selected"
                                  : "Select"
                              }}
                            </button>
                            <ChevronDown
                              class="w-4 h-4 text-muted-foreground transition-transform"
                              :class="{
                                'rotate-180':
                                  expandedWorkspaceId === workspace.id,
                              }"
                            />
                          </div>
                        </div>

                        <!-- Workspace Details (Expanded) -->
                        <div
                          v-if="expandedWorkspaceId === workspace.id"
                          class="mt-4 pt-4 border-t border-border space-y-4 animate-in slide-in-from-top-2 duration-200"
                        >
                          <div>
                            <p
                              class="text-[13px] font-semibold text-foreground mb-1"
                            >
                              Description
                            </p>
                            <p
                              class="text-[12px] text-muted-foreground leading-relaxed"
                            >
                              {{
                                workspace.description ||
                                "A professional workspace designed for collaboration and productivity."
                              }}
                            </p>
                          </div>
                          <div v-if="workspace.amenities?.length">
                            <p
                              class="text-[13px] font-semibold text-foreground mb-2"
                            >
                              Amenities
                            </p>
                            <div class="flex flex-wrap gap-1.5">
                              <span
                                v-for="amenity in workspace.amenities"
                                :key="amenity"
                                class="px-2 py-0.5 rounded-md bg-muted/50 text-[11px] text-muted-foreground"
                              >
                                {{ amenity }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  class="sticky bottom-0 bg-card py-4 border-t border-border mt-auto"
                >
                  <div class="relative flex items-center group">
                    <input
                      v-model="editForm.topic"
                      type="text"
                      class="w-full bg-muted/30 border border-border rounded-full py-3.5 pl-5 pr-14 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                      placeholder="What do you want to learn today?"
                      @keydown.enter.prevent="submitEditGoal"
                    />
                    <button
                      type="button"
                      @click="submitEditGoal"
                      class="absolute right-1.5 w-10 h-10 flex items-center justify-center bg-black dark:bg-white text-white dark:text-black rounded-full hover:opacity-90 transition-all shadow-sm"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                        <polyline points="12 5 19 12 12 19"></polyline>
                      </svg>
                    </button>
                  </div>
                </div>
              </form>

              <transition name="fade">
                <div v-if="isGeneratingPlan" class="mt-8 space-y-4">
                  <div
                    class="p-4 bg-muted/50 dark:bg-muted/30 rounded-xl border border-border"
                  >
                    <div class="flex items-start gap-3">
                      <div
                        class="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-foreground mt-0.5"
                      ></div>
                      <div class="flex-1">
                        <p
                          class="text-[15px] font-semibold text-foreground mb-1"
                        >
                          Regenerating learning plan...
                        </p>
                        <p class="text-[13px] text-muted-foreground">
                          This may take a while. We're creating a personalized
                          learning path just for you.
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    @click="cancelEdit"
                    class="w-full py-3 px-4 bg-transparent border border-border text-foreground rounded-full hover:bg-muted transition-all font-semibold text-sm"
                  >
                    Cancel Edit
                  </button>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- Main Content Area -->
    <div class="flex-1 min-w-0 lg:h-full lg:overflow-y-auto custom-scrollbar">
      <div v-if="loading" class="flex justify-center items-center py-20">
        <Loader />
      </div>

      <div v-else>
        <!-- Failure Banner -->
        <div
          v-if="
            (goal?.status || '').toLowerCase() === 'failed' ||
            (goal?.learning_tree?.status || '').toLowerCase() === 'failed'
          "
          class="mb-6 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4"
        >
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <svg
                class="w-5 h-5 text-red-600 dark:text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div class="flex-1">
              <h3 class="text-sm font-medium text-red-800 dark:text-red-300">
                Plan Generation Failed
              </h3>
              <div class="mt-1">
                <p class="text-sm text-red-700 dark:text-red-400">
                  We couldn't generate a learning plan for this goal. Please try
                  adjusting your details or retry the generation.
                </p>
                <div v-if="goal.learning_tree?.error || goal.failure_reason">
                  <button
                    type="button"
                    @click="showTechnicalError = !showTechnicalError"
                    class="mt-2 text-xs font-semibold text-red-800 dark:text-red-300 hover:underline flex items-center gap-1"
                  >
                    {{ showTechnicalError ? "Hide" : "Show" }} technical details
                    <ChevronDown
                      class="w-3 h-3 transition-transform duration-200"
                      :class="{ 'rotate-180': showTechnicalError }"
                    />
                  </button>
                  <div
                    v-if="showTechnicalError"
                    class="mt-2 p-3 bg-red-100/50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800/50"
                  >
                    <code
                      class="text-[10px] sm:text-xs text-red-900 dark:text-red-200 whitespace-pre-wrap break-all block leading-relaxed"
                    >
                      {{ goal.learning_tree?.error || goal.failure_reason }}
                    </code>
                  </div>
                </div>
              </div>
              <div class="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  @click="onEditGoal"
                  class="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 px-4 py-2 rounded-md text-sm font-medium hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                >
                  Edit & Retry
                </button>
                <button
                  type="button"
                  @click="openDeleteModal"
                  class="text-red-700 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm font-medium px-2 py-2"
                >
                  Delete Goal
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          class="hr-container sticky top-0 z-30 bg-gray-50 dark:bg-background py-4 mb-2 space-y-8"
        >
          <div class="flex items-start justify-between">
            <div class="space-y-3 flex-1">
              <p class="font-semibold text-foreground text-[36px]">
                {{ goal?.topic || "Learning Goal" }}
              </p>
            </div>
          </div>
          <div
            class="flex flex-col lg:flex-row items-center gap-4 flex-wrap w-full"
          >
            <div class="flex flex-wrap items-center gap-4">
              <Tabs v-model="activeTab" class="">
                <TabsList>
                  <TabsTrigger v-for="tab in tabs" :key="tab" :value="tab">
                    {{ tab }}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <LearningGoalActionsBar
                ref="actionsBar"
                v-if="goal"
                :goal="goal"
                :projectId="projectId"
                @book-mentor="onBookMentor"
                @edit-goal="onEditGoal"
                @delete="deleteModalOpen = true"
              />
            </div>

            <div class="flex items-center gap-2 flex-wrap lg:ml-auto">
              <button
                type="button"
                @click="onStartAssessment"
                class="py-2 px-4 bg-primaryDark text-[14px] hover:bg-primaryDarkBg dark:bg-muted/50 dark:hover:bg-muted/70 text-white font-bold rounded-full"
              >
                Start Assessment
              </button>
              <button
                type="button"
                class="py-2 px-4 text-[14px] font-medium bg-background text-foreground border border-border rounded-full hover:bg-muted"
              >
                Download Certificate
              </button>
              <div class="relative" v-click-outside="closeActionsDropdown">
                <button
                  type="button"
                  @click="toggleActionsDropdown"
                  class="w-10 h-10 rounded-full bg-transparent border border-border flex items-center justify-center hover:bg-muted hover:text-muted-foreground transition-colors"
                >
                  <EllipsisVertical class="w-5 h-5" />
                </button>

                <transition name="fade">
                  <div
                    v-if="showActionsDropdown"
                    class="absolute right-0 mt-2 w-48 bg-card border border-border rounded-[12px] shadow-lg py-1 z-50 overflow-hidden"
                  >
                    <button
                      @click="openDeleteModal"
                      class="w-full flex items-center gap-2 px-4 py-3 text-[14px] text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors text-left"
                    >
                      <Trash2 class="w-4 h-4" />
                      Delete Goal
                    </button>
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>

        <!-- Regeneration Skeleton (Internal to Header) -->
        <div v-if="isGeneratingPlan" class="space-y-6 animate-pulse mt-8">
          <div
            class="border border-gray-200 dark:border-border rounded-xl p-6 space-y-4"
          >
            <div class="h-4 bg-gray-200 dark:bg-muted rounded w-full"></div>
            <div class="h-4 bg-gray-200 dark:bg-muted rounded w-5/6"></div>
            <div class="h-4 bg-gray-200 dark:bg-muted rounded w-4/6"></div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div
              v-for="i in 6"
              :key="i"
              class="border border-gray-200 dark:border-border rounded-xl p-6 space-y-3"
            >
              <div class="h-6 bg-gray-200 dark:bg-muted rounded w-3/4"></div>
              <div class="h-4 bg-gray-200 dark:bg-muted rounded w-full"></div>
              <div class="h-4 bg-gray-200 dark:bg-muted rounded w-2/3"></div>
            </div>
          </div>
        </div>

        <!-- TAB CONTENT AREA -->
        <div v-else>
          <div v-if="activeTab === 'Overview'">
            <div
              class="mt-2 w-full min-w-0 bg-transparent border border-border rounded-[12px] p-3"
            >
              <div class="items-start space-y-4">
                <h2 class="text-[15px] font-semibold text-foreground">
                  Description
                </h2>
                <p class="text-foreground text-[14px] flex-1 break-words">
                  {{ goal?.description || defaultDescription }}
                </p>
                <div>
                  <h2
                    class="text-[15px] font-semibold border-b border-gray-200 text-foreground py-4 mb-4"
                  >
                    Mentor
                  </h2>
                  <div
                    v-if="goal?.mentor && selectedMentorDetails"
                    class="mt-4 p-4 rounded-[16px] border border-border bg-card dark:bg-card hover:shadow-md transition-shadow group"
                  >
                    <div class="flex items-center justify-between gap-4">
                      <div class="flex items-center gap-4">
                        <div
                          class="relative w-14 h-14 rounded-full overflow-hidden bg-muted border-2 border-background shadow-sm"
                        >
                          <img
                            v-if="
                              selectedMentorDetails?.profile_picture_thumbnail ||
                              selectedMentorDetails?.profile_picture
                            "
                            :src="
                              selectedMentorDetails?.profile_picture_thumbnail ||
                              selectedMentorDetails?.profile_picture
                            "
                            :alt="selectedMentorDetails?.name || 'Mentor'"
                            class="w-full h-full object-cover"
                          />
                          <div
                            v-else
                            class="w-full h-full flex items-center justify-center bg-primary/10 text-primary font-bold text-lg"
                          >
                            {{
                              (selectedMentorDetails?.name ||
                                selectedMentorDetails?.first_name ||
                                "M")[0].toUpperCase()
                            }}
                          </div>
                        </div>
                        <div>
                          <div class="flex items-center gap-2">
                            <h3 class="text-[16px] font-bold text-foreground">
                              {{
                                selectedMentorDetails?.name ||
                                `${selectedMentorDetails?.first_name || ""} ${
                                  selectedMentorDetails?.last_name || ""
                                }`.trim()
                              }}
                            </h3>
                            <span
                              class="px-2 py-0.5 rounded-full bg-primary/10 text-[10px] text-primary font-bold uppercase tracking-wider"
                              >Assigned Mentor</span
                            >
                            <span
                              class="px-2 py-0.5 rounded-full bg-primary/10 text-[10px] text-primary font-bold capitalize"
                              >{{ goal.mentor_status }}</span
                            >
                          </div>
                          <p class="text-[13px] text-muted-foreground mt-0.5">
                            Expert in {{ goal.topic || "this field" }}
                          </p>
                        </div>
                      </div>

                      <button
                        @click="onBookMentor"
                        class="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-full text-[13px] font-bold hover:opacity-90 transition-all shadow-sm"
                      >
                        Book a Session
                      </button>
                    </div>
                  </div>
                  <div v-else>
                    <div
                      class="text-center py-10 bg-card border border-dashed border-border rounded-xl"
                    >
                      <p class="text-sm font-medium text-foreground">
                        No mentor assigned to this goal yet
                      </p>
                    </div>
                  </div>
                </div>

                <!-- <button
                type="button"
                class="h-6 w-6 flex-shrink-0 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-muted transition-colors"
                @click="toggleDescription"
                :aria-expanded="descriptionExpanded.toString()"
                aria-label="Toggle description"
              >
                <svg
                  :class="{ 'rotate-180': descriptionExpanded }"
                  class="transition-transform"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    class="text-muted-foreground"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button> -->
              </div>

              <div>
                <h2
                  class="text-[15px] font-semibold border-b border-gray-200 text-foreground py-4 mb-4"
                >
                  Workspaces
                </h2>
                <div
                  v-if="goal && goal.workspace_id"
                  class="mt-8 border border-border rounded-[12px] p-6 bg-card"
                >
                  <div
                    v-if="workspaceDetails || (goal && goal.workspace_name)"
                    class="mb-6 bg-muted/30 border rounded-[12px] p-4 flex items-center justify-between border-green-500/30"
                  >
                    <div class="flex items-center gap-4">
                      <!-- Workspace Image -->
                      <div
                        class="relative w-16 h-16 rounded-lg overflow-hidden bg-primary/10 flex-shrink-0"
                      >
                        <img
                          v-if="
                            workspaceDetails &&
                            workspaceDetails.images &&
                            workspaceDetails.images[0]
                          "
                          :src="workspaceDetails.images[0]"
                          alt="Workspace"
                          class="w-full h-full object-cover"
                        />
                        <div
                          v-else
                          class="w-full h-full flex items-center justify-center"
                        >
                          <span class="text-xl font-semibold text-primary">
                            {{
                              workspaceDetails && workspaceDetails.name
                                ? workspaceDetails.name.charAt(0)
                                : goal.workspace_name
                                ? goal.workspace_name.charAt(0)
                                : "W"
                            }}
                          </span>
                        </div>
                      </div>

                      <!-- Workspace Info -->
                      <div>
                        <p class="text-[15px] font-semibold text-foreground">
                          {{
                            (workspaceDetails && workspaceDetails.name) ||
                            (goal && goal.workspace_name)
                          }}
                        </p>
                        <p
                          v-if="workspaceDetails && workspaceDetails.address"
                          class="text-[12px] text-muted-foreground mt-0.5"
                        >
                          {{ workspaceDetails.address }}
                        </p>
                        <p v-else class="text-[12px] text-muted-foreground">
                          Physical Workspace
                        </p>

                        <!-- Amenities (Optional) -->
                        <div
                          v-if="
                            workspaceDetails &&
                            workspaceDetails.amenities &&
                            workspaceDetails.amenities.length
                          "
                          class="flex items-center gap-2 mt-2"
                        >
                          <span
                            v-for="amenity in workspaceDetails.amenities.slice(
                              0,
                              3,
                            )"
                            :key="amenity"
                            class="text-[10px] px-2 py-0.5 bg-background border border-border rounded-full text-muted-foreground"
                          >
                            {{ amenity }}
                          </span>
                          <span
                            v-if="workspaceDetails.amenities.length > 3"
                            class="text-[10px] text-muted-foreground"
                          >
                            +{{ workspaceDetails.amenities.length - 3 }} more
                          </span>
                        </div>
                      </div>
                    </div>
                    <div
                      class="px-3 py-1.5 rounded-full text-[12px] font-semibold border-2 border-green-600 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 dark:border-green-500 flex-shrink-0 ml-4"
                    >
                      Selected
                    </div>
                  </div>
                </div>
                <div v-else>
                  <div
                    class="text-center py-10 bg-card border border-dashed border-border rounded-xl"
                  >
                    <p class="text-sm font-medium text-foreground">
                      No workspace assigned to this goal yet
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="activeTab === 'Modules'" class="space-y-6">
            <div class="mt-4 border border-border rounded-[12px] p-2 md:p-6">
              <div
                class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6"
              >
                <h2 class="text-[15px] font-semibold text-foreground">
                  Finish each action to unlock the next
                </h2>
                <div
                  class="flex items-center gap-2 text-sm font-semibold text-foreground/80 bg-muted/40 px-4 py-2 rounded-full border border-border self-start"
                >
                  <span
                    >Estimated Completion Time :
                    {{
                      goal?.estimated_duration_weeks ||
                      goal?.metadata?.estimated_duration_weeks ||
                      0
                    }}
                    Weeks</span
                  >
                </div>
              </div>

              <!-- Empty State -->
              <div
                v-if="!learningModules.length"
                class="text-center py-8 bg-card border border-border rounded-[12px]"
              >
                <p class="text-muted-foreground text-[14px]">
                  No learning modules created yet. This might take a little
                  while.
                </p>
              </div>

              <!-- Accordion Sections Container -->
              <div v-else class="space-y-4">
                <!-- LESSONS SECTION -->
                <div
                  class="border border-border rounded-[12px] overflow-hidden bg-card transition-all shadow-sm"
                >
                  <!-- Section Header -->
                  <div
                    class="flex flex-col md:flex-row md:items-center justify-between cursor-pointer hover:bg-muted/5 transition-colors"
                    @click="toggleSection('lessons')"
                  >
                    <div class="flex items-stretch flex-grow min-w-0">
                      <!-- Left Colored Prefix Block -->
                      <div
                        class="w-16 md:w-20 bg-[#E6F7F0] dark:bg-green-950/20 text-[#00B074] flex items-center justify-center flex-shrink-0 self-stretch min-h-[80px]"
                      >
                        <BookOpen class="w-6 h-6 md:w-8 md:h-8" />
                      </div>

                      <!-- Title & Description -->
                      <div class="p-6 flex-grow min-w-0">
                        <div class="flex items-center gap-2">
                          <h3
                            class="text-[18px] font-extrabold text-foreground flex items-center gap-1.5"
                          >
                            Lessons
                            <ChevronDown
                              v-if="expandedSections.includes('lessons')"
                              class="w-4 h-4 text-muted-foreground"
                            />
                            <ChevronRight
                              v-else
                              class="w-4 h-4 text-muted-foreground"
                            />
                          </h3>
                        </div>
                        <p
                          class="text-[14px] text-muted-foreground mt-1 line-clamp-2"
                        >
                          Master the core concepts through expert-led videos,
                          readings, and interactive lessons.
                        </p>
                      </div>
                    </div>

                    <!-- Right Side: Badge & Action Button -->
                    <div
                      class="px-6 pb-6 md:pb-0 flex items-center gap-4 flex-shrink-0 self-center"
                    >
                      <button
                        v-if="getLessonsStatus() !== 'Completed'"
                        @click.stop="continueLearning"
                        class="px-4 py-2 bg-[#2D3139] hover:bg-[#1a1d22] text-white rounded-full font-bold text-[14px] shadow-sm transition-all whitespace-nowrap"
                      >
                        Continue
                      </button>
                      <span
                        class="px-4 py-2 rounded-full text-[14px] font-extrabold border"
                        :class="{
                          'bg-[#E6F7F0] text-[#00B074] border-[#B3EBD6]':
                            getLessonsStatus() === 'Completed',
                          'bg-[#FFF9E6] text-[#FFA800] border-[#FFE8B3]':
                            getLessonsStatus() === 'In progress',
                          'bg-[#F2F2F2] text-[#8C8C8C] border-[#D9D9D9]':
                            getLessonsStatus() === 'Not started',
                        }"
                      >
                        {{ getLessonsStatus() }}
                      </span>
                    </div>
                  </div>

                  <!-- Accordion Content: Modules & Topics List -->
                  <div
                    v-if="expandedSections.includes('lessons')"
                    class="border-t border-border bg-muted/5 p-6 space-y-8"
                  >
                    <div
                      v-for="(module, mIndex) in learningModules"
                      :key="module.task_id || mIndex"
                      class="space-y-4"
                    >
                      <!-- Module Heading -->
                      <div class="space-y-1">
                        <h4 class="text-[16px] font-bold text-foreground">
                          {{ module.title }}
                        </h4>
                        <p class="text-[13px] text-muted-foreground">
                          {{ module.description }}
                        </p>
                      </div>

                      <!-- Topics Grid -->
                      <div
                        class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
                      >
                        <div
                          v-for="(subTask, sIndex) in getSubTasks(module)"
                          :key="subTask.task_id || sIndex"
                          class="bg-card border border-border/80 rounded-[16px] p-5 flex items-center justify-between hover:shadow-md transition-all cursor-pointer group h-full"
                          :class="{
                            'opacity-60 cursor-not-allowed hover:shadow-none':
                              !isSubTaskUnlocked(mIndex, sIndex) &&
                              !isTaskCompleted(subTask),
                          }"
                          @click="
                            handleSubTaskClick(module, mIndex, subTask, sIndex)
                          "
                        >
                          <!-- Left: Icon -->
                          <div
                            class="w-10 h-10 rounded-full bg-muted/50 border border-border flex items-center justify-center flex-shrink-0 mr-4"
                          >
                            <component
                              :is="getSubTaskIcon(subTask)"
                              class="w-5 h-5 text-muted-foreground"
                              stroke-width="1.5"
                            />
                          </div>

                          <!-- Center: Content -->
                          <div class="flex-grow min-w-0 pr-2">
                            <h4
                              class="text-[14px] font-bold text-foreground leading-snug line-clamp-2 group-hover:text-primary transition-colors"
                              :class="{
                                'text-muted-foreground': !isSubTaskUnlocked(
                                  mIndex,
                                  sIndex,
                                ),
                              }"
                            >
                              {{ subTask.title }}
                            </h4>
                            <div
                              class="flex items-center flex-wrap gap-y-1 text-[12px] text-muted-foreground/80 mt-1 font-medium"
                            >
                              <span
                                >{{ getSubTaskLinksCount(subTask) }} links</span
                              >
                              <span class="mx-2 text-border">|</span>
                              <span>{{
                                getSubTaskDurationLabel(subTask)
                              }}</span>
                              <span
                                v-if="subTask.practice_task"
                                class="mx-2 text-border"
                                >|</span
                              >
                              <span v-if="subTask.practice_task"
                                >1 practice task</span
                              >
                            </div>
                          </div>

                          <!-- Right: Status Check / Lock -->
                          <div class="flex-shrink-0 pl-2">
                            <div
                              v-if="isTaskCompleted(subTask)"
                              class="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center text-white"
                            >
                              <Check class="w-4 h-4 text-background" />
                            </div>

                            <Lock
                              v-else-if="!isSubTaskUnlocked(mIndex, sIndex)"
                              class="w-4 h-4 text-muted-foreground/45"
                            />

                            <div v-else class="w-6 h-6" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- PROJECTS SECTION -->
                <div
                  class="border border-border rounded-[12px] overflow-hidden bg-card transition-all shadow-sm"
                >
                  <!-- Section Header -->
                  <div
                    class="flex flex-col md:flex-row md:items-center justify-between cursor-pointer hover:bg-muted/5 transition-colors"
                    :class="{
                      'opacity-50 grayscale':
                        getLessonsStatus() !== 'Completed',
                    }"
                    @click="toggleSection('projects')"
                  >
                    <div class="flex items-stretch flex-grow min-w-0">
                      <!-- Left Colored Prefix Block -->
                      <div
                        class="w-16 md:w-20 bg-[#FFF9E6] dark:bg-yellow-950/20 text-[#FFA800] flex items-center justify-center flex-shrink-0 self-stretch min-h-[80px]"
                      >
                        <Rocket class="w-6 h-6 md:w-8 md:h-8" />
                      </div>

                      <!-- Title & Description -->
                      <div class="p-6 flex-grow min-w-0">
                        <div class="flex items-center gap-2">
                          <h3
                            class="text-[18px] font-extrabold text-foreground flex items-center gap-1.5"
                          >
                            Projects
                            <Lock
                              v-if="getLessonsStatus() !== 'Completed'"
                              class="w-4 h-4 text-muted-foreground"
                            />
                            <template v-else>
                              <ChevronDown
                                v-if="expandedSections.includes('projects')"
                                class="w-4 h-4 text-muted-foreground"
                              />
                              <ChevronRight
                                v-else
                                class="w-4 h-4 text-muted-foreground"
                              />
                            </template>
                          </h3>
                        </div>
                        <p
                          class="text-[14px] text-muted-foreground mt-1 line-clamp-2"
                        >
                          Apply your knowledge to real-world scenarios by
                          building portfolio-ready capstone projects.
                        </p>
                      </div>
                    </div>

                    <!-- Right Side: Badge -->
                    <div
                      class="px-6 pb-6 md:pb-0 flex items-center gap-4 flex-shrink-0 self-center"
                    >
                      <span
                        class="px-4 py-2 rounded-full text-[14px] font-extrabold border"
                        :class="{
                          'bg-[#E6F7F0] text-[#00B074] border-[#B3EBD6]':
                            getProjectsStatus() === 'Completed',
                          'bg-[#FFF9E6] text-[#FFA800] border-[#FFE8B3]':
                            getProjectsStatus() === 'In progress',
                          'bg-[#F2F2F2] text-[#8C8C8C] border-[#D9D9D9]':
                            getProjectsStatus() === 'Not started',
                        }"
                      >
                        {{ getProjectsStatus() }}
                      </span>
                    </div>
                  </div>

                  <!-- Accordion Content -->
                  <transition name="fade">
                    <div
                      v-if="expandedSections.includes('projects')"
                      class="border-t border-border bg-muted/5 p-6 space-y-6"
                    >
                      <div
                        v-if="capstoneProjects.length === 0"
                        class="text-center py-8 text-muted-foreground"
                      >
                        No sub-projects available for this learning goal.
                      </div>

                      <div
                        v-for="project in capstoneProjects"
                        :key="project.task_id"
                        class="bg-card border border-border rounded-[20px] p-6 lg:p-8 shadow-sm"
                      >
                        <h4 class="text-[20px] font-bold text-foreground mb-4">
                          {{ project.practice_task?.title || project.title }}
                        </h4>
                        <p
                          class="text-[15px] text-foreground/80 leading-relaxed mb-6"
                        >
                          {{
                            project.practice_task?.context ||
                            project.description
                          }}
                        </p>

                        <div class="mb-6">
                          <h5
                            class="text-[15px] font-bold text-foreground mb-2"
                          >
                            Deliverable
                          </h5>
                          <p
                            class="text-[14px] text-muted-foreground leading-relaxed"
                          >
                            {{ project.practice_task?.deliverable }}
                          </p>
                        </div>

                        <div class="mb-6">
                          <h5
                            class="text-[15px] font-bold text-foreground mb-2"
                          >
                            Criteria
                          </h5>
                          <ul
                            class="list-disc list-outside ml-5 text-[14px] text-muted-foreground space-y-1.5"
                          >
                            <li
                              v-for="(criterion, index) in project.practice_task
                                ?.acceptance_criteria"
                              :key="index"
                            >
                              {{ criterion }}
                            </li>
                          </ul>
                        </div>

                        <div class="mb-8">
                          <h5
                            class="text-[15px] font-bold text-foreground mb-2"
                          >
                            Submission Requirements:
                          </h5>
                          <p
                            class="text-[14px] text-muted-foreground space-y-1.5"
                          >
                            {{
                              project.practice_task?.submission_format ||
                              "Submit your project link or files."
                            }}
                          </p>
                        </div>

                        <div
                          v-if="
                            project.project_submission ||
                            project.task_status === 'Completed'
                          "
                          class="mt-8 pt-8 border-t border-border"
                        >
                          <h4
                            class="text-[18px] font-bold text-foreground mb-4 flex items-center gap-2"
                          >
                            <Check class="w-5 h-5 text-green-500" />
                            Project Submitted & Evaluated
                          </h4>

                          <div
                            v-if="project.project_submission"
                            class="bg-muted/30 p-6 rounded-[12px] border border-border/50 space-y-6"
                          >
                            <div
                              class="flex items-center justify-between border-b border-border/50 pb-4"
                            >
                              <span
                                class="text-[14px] font-semibold text-muted-foreground"
                                >AI Review Score</span
                              >
                              <div
                                class="flex items-center justify-center w-12 h-12 rounded-full font-bold text-[18px]"
                                :class="
                                  project.project_submission.ai_evaluation
                                    ?.score >= 80
                                    ? 'bg-green-100 text-green-700'
                                    : project.project_submission.ai_evaluation
                                        ?.score >= 60
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-red-100 text-red-700'
                                "
                              >
                                {{
                                  project.project_submission.ai_evaluation
                                    ?.score || 0
                                }}
                              </div>
                            </div>

                            <div>
                              <h5
                                class="text-[15px] font-bold text-foreground mb-3 flex items-center gap-2"
                              >
                                <Award class="w-4 h-4 text-primary" />
                                Key Strengths
                              </h5>
                              <ul class="space-y-2">
                                <li
                                  v-for="(strength, idx) in project
                                    .project_submission.ai_evaluation
                                    ?.strengths || []"
                                  :key="'s-' + idx"
                                  class="flex items-start gap-2 text-[14px] text-muted-foreground"
                                >
                                  <Check
                                    class="w-4 h-4 text-green-500 mt-0.5 shrink-0"
                                  />
                                  <span>{{ strength }}</span>
                                </li>
                              </ul>
                            </div>

                            <div
                              v-if="
                                project.project_submission.ai_evaluation
                                  ?.areas_for_improvement?.length
                              "
                            >
                              <h5
                                class="text-[15px] font-bold text-foreground mb-3 flex items-center gap-2"
                              >
                                <Rocket class="w-4 h-4 text-blue-500" />
                                Areas for Improvement
                              </h5>
                              <ul class="space-y-2">
                                <li
                                  v-for="(area, idx) in project
                                    .project_submission.ai_evaluation
                                    ?.areas_for_improvement"
                                  :key="'a-' + idx"
                                  class="flex items-start gap-2 text-[14px] text-muted-foreground"
                                >
                                  <ChevronRight
                                    class="w-4 h-4 text-blue-500 mt-0.5 shrink-0"
                                  />
                                  <span>{{ area }}</span>
                                </li>
                              </ul>
                            </div>

                            <div>
                              <h5
                                class="text-[15px] font-bold text-foreground mb-2"
                              >
                                Detailed Feedback
                              </h5>
                              <p
                                class="text-[14px] text-muted-foreground leading-relaxed whitespace-pre-wrap"
                              >
                                {{
                                  project.project_submission.ai_evaluation
                                    ?.detailed_feedback
                                }}
                              </p>
                            </div>
                          </div>
                          <div
                            v-else
                            class="bg-muted/30 p-6 rounded-[12px] border border-border/50"
                          >
                            <p class="text-[14px] text-muted-foreground">
                              Your project was submitted before the detailed AI
                              review system was introduced. Resubmit your
                              project to receive an in-depth AI evaluation!
                            </p>
                          </div>
                        </div>

                        <div class="mt-6">
                          <button
                            class="bg-[#3D4045] hover:bg-[#2A2C30] text-white px-8 py-3 rounded-full text-[14px] font-semibold transition-colors"
                            @click="handleProjectClick(project)"
                          >
                            {{
                              project.project_submission ||
                              project.task_status === "Completed"
                                ? "Resubmit Project"
                                : "Submit Sub-Project"
                            }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </transition>
                </div>

                <!-- ASSESSMENT SECTION -->
                <div
                  class="border border-border rounded-[12px] overflow-hidden bg-card transition-all shadow-sm"
                >
                  <!-- Section Header -->
                  <div
                    class="flex flex-col md:flex-row md:items-center justify-between cursor-pointer hover:bg-muted/5 transition-colors"
                    :class="{
                      'opacity-50 grayscale':
                        getLessonsStatus() !== 'Completed' ||
                        getProjectsStatus() !== 'Completed',
                    }"
                    @click="toggleSection('assessment')"
                  >
                    <div class="flex items-stretch flex-grow min-w-0">
                      <!-- Left Colored Prefix Block -->
                      <div
                        class="w-16 md:w-20 bg-[#F2F2F2] dark:bg-gray-800/20 text-[#8C8C8C] flex items-center justify-center flex-shrink-0 self-stretch min-h-[80px]"
                      >
                        <Award class="w-6 h-6 md:w-8 md:h-8" />
                      </div>

                      <!-- Title & Description -->
                      <div class="p-6 flex-grow min-w-0">
                        <div class="flex items-center gap-2">
                          <h3
                            class="text-[18px] font-extrabold text-foreground flex items-center gap-1.5"
                          >
                            Assessment
                            <Lock
                              v-if="
                                getLessonsStatus() !== 'Completed' ||
                                getProjectsStatus() !== 'Completed'
                              "
                              class="w-4 h-4 text-muted-foreground"
                            />
                            <template v-else>
                              <ChevronDown
                                v-if="expandedSections.includes('assessment')"
                                class="w-4 h-4 text-muted-foreground"
                              />
                              <ChevronRight
                                v-else
                                class="w-4 h-4 text-muted-foreground"
                              />
                            </template>
                          </h3>
                        </div>
                        <p
                          class="text-[14px] text-muted-foreground mt-1 line-clamp-2"
                        >
                          Complete the final test to unlock your certification.
                        </p>
                      </div>
                    </div>

                    <!-- Right Side: Badge -->
                    <div
                      class="px-6 pb-6 md:pb-0 flex items-center gap-4 flex-shrink-0 self-center"
                    >
                      <span
                        class="px-4 py-2 rounded-full text-[14px] font-extrabold border"
                        :class="{
                          'bg-[#E6F7F0] text-[#00B074] border-[#B3EBD6]':
                            getAssessmentStatus() === 'Completed',
                          'bg-[#FFF9E6] text-[#FFA800] border-[#FFE8B3]':
                            getAssessmentStatus() === 'In progress',
                          'bg-[#F2F2F2] text-[#8C8C8C] border-[#D9D9D9]':
                            getAssessmentStatus() === 'Not started',
                        }"
                      >
                        {{ getAssessmentStatus() }}
                      </span>
                    </div>
                  </div>

                  <!-- Accordion Content -->
                  <transition name="fade">
                    <div
                      v-if="expandedSections.includes('assessment')"
                      class="border-t border-border bg-muted/5 p-6"
                    >
                      <div
                        class="text-center py-10 bg-card border border-dashed border-border rounded-2xl space-y-4"
                      >
                        <p class="text-[14px] font-bold text-foreground">
                          Complete the final test to unlock your certification.
                        </p>
                        <button
                          v-if="goal?.has_assessment"
                          type="button"
                          @click="onStartAssessment"
                          class="py-2.5 px-6 bg-primaryDark text-[14px] hover:bg-primaryDarkBg dark:bg-muted/50 dark:hover:bg-muted/70 text-white font-bold rounded-full transition-all"
                        >
                          Start Assessment
                        </button>
                        <p
                          v-else
                          class="text-[13px] text-muted-foreground font-medium"
                        >
                          Assessment not available yet.
                        </p>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
            </div>
          </div>

          <!-- Notes Tab -->
          <div v-if="activeTab === 'Notes'" class="space-y-4">
            <div class="mt-4 border border-border rounded-[12px] p-2 md:p-6">
              <!-- Notes Header -->
              <div
                class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6"
              >
                <h3 class="text-lg font-semibold text-foreground">
                  Your Notes
                </h3>
                <div
                  class="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1 justify-end"
                >
                  <div class="relative w-full sm:w-56">
                    <input
                      v-model="notesSearchQuery"
                      type="text"
                      placeholder="| Search notes..."
                      style="border-radius: 20px"
                      class="w-full pl-8 pr-4 py-2.5 bg-background dark:bg-muted border-none text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 text-[12px]"
                    />
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      class="absolute left-3 top-1/2 transform -translate-y-1/2"
                    >
                      <path
                        d="M7.66536 13.9997C11.1632 13.9997 13.9987 11.1641 13.9987 7.66634C13.9987 4.16854 11.1632 1.33301 7.66536 1.33301C4.16756 1.33301 1.33203 4.16854 1.33203 7.66634C1.33203 11.1641 4.16756 13.9997 7.66536 13.9997Z"
                        class="text-muted-foreground"
                        stroke-width="1.2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M14.6654 14.6663L13.332 13.333"
                        class="text-muted-foreground"
                        stroke-width="1.2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <button
                    v-if="!isCreatingNote"
                    @click="openNoteEditor()"
                    class="px-6 py-2.5 bg-[#4B4B4B] dark:bg-primary text-white dark:text-primary-foreground rounded-full hover:opacity-90 transition-opacity whitespace-nowrap text-[13px] font-medium"
                  >
                    Create New Note
                  </button>
                </div>
              </div>

              <!-- Empty State -->
              <div
                v-if="
                  !isCreatingNote &&
                  !loadingNotes &&
                  filteredNotes.length === 0 &&
                  !notesSearchQuery
                "
                class="flex flex-col items-center justify-center py-20 rounded-lg"
              >
                <div
                  class="w-16 h-16 mb-4 rounded-full border border-border flex items-center justify-center"
                >
                  <svg
                    width="36"
                    height="36"
                    viewBox="0 0 36 36"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <mask
                      id="mask0_23808_43691"
                      style="mask-type: luminance"
                      maskUnits="userSpaceOnUse"
                      x="0"
                      y="0"
                      width="36"
                      height="36"
                    >
                      <path d="M36 0H0V36H36V0Z" fill="white" />
                    </mask>
                    <g mask="url(#mask0_23808_43691)">
                      <path
                        d="M33 15V22.5C33 30 30 33 22.5 33H13.5C6 33 3 30 3 22.5V13.5C3 6 6 3 13.5 3H21"
                        class="text-muted-foreground"
                        stroke-opacity="0.6"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M33 15H27C22.5 15 21 13.5 21 9V3L33 15Z"
                        class="text-muted-foreground"
                        stroke-opacity="0.6"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M10.5 19.5H19.5"
                        class="text-muted-foreground"
                        stroke-opacity="0.6"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M10.5 25.5H16.5"
                        class="text-muted-foreground"
                        stroke-opacity="0.6"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </g>
                  </svg>
                </div>
                <p class="text-muted-foreground text-center">
                  There are currently no notes saved
                </p>
              </div>

              <!-- No Search Results -->
              <div
                v-if="
                  !isCreatingNote &&
                  !loadingNotes &&
                  filteredNotes.length === 0 &&
                  notesSearchQuery
                "
                class="flex flex-col items-center justify-center py-20 rounded-lg"
              >
                <div
                  class="w-16 h-16 mb-4 rounded-full border border-border flex items-center justify-center"
                >
                  <FileText class="w-8 h-8 text-muted-foreground" />
                </div>
                <p class="text-muted-foreground text-center">
                  No notes found matching "{{ notesSearchQuery }}"
                </p>
              </div>

              <!-- Loading State -->
              <div v-if="loadingNotes && !isCreatingNote" class="space-y-4">
                <div
                  v-for="i in 3"
                  :key="i"
                  class="animate-pulse rounded-lg border border-border p-4"
                >
                  <div class="h-5 bg-muted rounded w-3/4 mb-2"></div>
                  <div class="h-4 bg-muted rounded w-1/2 mb-3"></div>
                  <div class="h-4 bg-muted rounded w-full"></div>
                </div>
              </div>

              <!-- Notes List -->
              <div
                v-if="
                  !isCreatingNote && !loadingNotes && filteredNotes.length > 0
                "
                class="space-y-3"
              >
                <div
                  v-for="note in filteredNotes"
                  :key="note.id"
                  class="rounded-lg border border-border p-4 hover:border-primary/50 transition-colors cursor-pointer group"
                  @click="openViewNoteModal(note)"
                >
                  <div class="flex items-start gap-4">
                    <div class="flex-shrink-0 mt-1">
                      <div
                        class="w-10 h-10 rounded border border-border flex items-center justify-center"
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <mask
                            id="mask0_23808_43751"
                            style="mask-type: luminance"
                            maskUnits="userSpaceOnUse"
                            x="0"
                            y="0"
                            width="24"
                            height="24"
                          >
                            <path d="M24 0H0V24H24V0Z" fill="white" />
                          </mask>
                          <g mask="url(#mask0_23808_43751)">
                            <path
                              d="M22 10V15C22 20 20 22 15 22H9C4 22 2 20 2 15V9C2 4 4 2 9 2H14"
                              class="text-muted-foreground"
                              stroke-opacity="0.72"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M22 10H18C15 10 14 9 14 6V2L22 10Z"
                              class="text-muted-foreground"
                              stroke-opacity="0.72"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M7 13H13"
                              class="text-muted-foreground"
                              stroke-opacity="0.72"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M7 17H11"
                              class="text-muted-foreground"
                              stroke-opacity="0.72"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </g>
                        </svg>
                      </div>
                    </div>
                    <div class="flex-1 min-w-0">
                      <h4 class="font-semibold text-foreground mb-1 truncate">
                        {{ note.title }}
                      </h4>
                      <p class="text-sm text-muted-foreground">
                        {{ formatNoteDate(note.created_at) }} •
                        {{ truncateContent(note.content) }}
                      </p>
                    </div>
                    <div class="relative flex-shrink-0">
                      <button
                        @click.stop="toggleNoteMenu(note.id)"
                        class="w-8 h-8 flex items-center justify-center rounded hover:bg-muted transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            cx="4"
                            cy="10"
                            r="1.5"
                            class="fill-muted-foreground"
                          />
                          <circle
                            cx="10"
                            cy="10"
                            r="1.5"
                            class="fill-muted-foreground"
                          />
                          <circle
                            cx="16"
                            cy="10"
                            r="1.5"
                            class="fill-muted-foreground"
                          />
                        </svg>
                      </button>

                      <!-- Note Menu Dropdown -->
                      <div
                        v-if="noteMenuOpen === note.id"
                        v-click-outside-note="closeNoteMenu"
                        class="absolute right-0 top-full mt-1 w-32 bg-background border border-border rounded-lg shadow-lg z-10 overflow-hidden"
                      >
                        <button
                          @click.stop="openViewNoteModal(note)"
                          class="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-2"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12 5C7 5 2.73 8.11 1 12.5C2.73 16.89 7 20 12 20C17 20 21.27 16.89 23 12.5C21.27 8.11 17 5 12 5Z"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M12 15.5C13.933 15.5 15.5 13.933 15.5 12C15.5 10.067 13.933 8.5 12 8.5C10.067 8.5 8.5 10.067 8.5 12C8.5 13.933 10.067 15.5 12 15.5Z"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                          View
                        </button>
                        <button
                          @click.stop="
                            openNoteEditor(note);
                            closeNoteMenu();
                          "
                          class="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-2"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                            <path
                              d="M18.5 2.50023C18.8978 2.1024 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1024 21.5 2.50023C21.8978 2.89805 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.1024 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                          Edit
                        </button>
                        <button
                          @click.stop="deleteNote(note)"
                          class="w-full px-4 py-2 text-left text-sm text-red-500 hover:bg-muted transition-colors flex items-center gap-2"
                        >
                          <Trash2 class="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Note Editor -->
              <div v-if="isCreatingNote" class="space-y-4">
                <div
                  class="rounded-[12px] border border-border bg-card p-5 space-y-4 flex flex-col shadow-sm"
                >
                  <!-- Formatting Toolbar & Save Button -->
                  <div class="flex items-center justify-between w-full">
                    <div
                      class="flex items-center gap-1 sm:gap-2 text-muted-foreground/60"
                    >
                      <button
                        @click="applyFormatting('bold')"
                        :class="[
                          'p-1.5 rounded transition-all',
                          editor?.isActive('bold')
                            ? 'text-primary bg-primary/10'
                            : 'hover:text-foreground hover:bg-muted/30',
                        ]"
                        title="Bold"
                      >
                        <Bold class="w-5 h-5 stroke-[1.5]" />
                      </button>
                      <button
                        @click="applyFormatting('italic')"
                        :class="[
                          'p-1.5 rounded transition-all',
                          editor?.isActive('italic')
                            ? 'text-primary bg-primary/10'
                            : 'hover:text-foreground hover:bg-muted/30',
                        ]"
                        title="Italic"
                      >
                        <Italic class="w-5 h-5 stroke-[1.5]" />
                      </button>
                      <button
                        @click="applyFormatting('bulletList')"
                        :class="[
                          'p-1.5 rounded transition-all',
                          editor?.isActive('bulletList')
                            ? 'text-primary bg-primary/10'
                            : 'hover:text-foreground hover:bg-muted/30',
                        ]"
                        title="Bullet List"
                      >
                        <List class="w-5 h-5 stroke-[1.5]" />
                      </button>
                      <button
                        @click="applyFormatting('alignLeft')"
                        :class="[
                          'p-1.5 rounded transition-all',
                          editor?.isActive({ textAlign: 'left' })
                            ? 'text-primary bg-primary/10'
                            : 'hover:text-foreground hover:bg-muted/30',
                        ]"
                        title="Align Left"
                      >
                        <AlignLeft class="w-5 h-5 stroke-[1.5]" />
                      </button>
                      <button
                        @click="applyFormatting('alignCenter')"
                        :class="[
                          'p-1.5 rounded transition-all',
                          editor?.isActive({ textAlign: 'center' })
                            ? 'text-primary bg-primary/10'
                            : 'hover:text-foreground hover:bg-muted/30',
                        ]"
                        title="Align Center"
                      >
                        <AlignCenter class="w-5 h-5 stroke-[1.5]" />
                      </button>
                      <button
                        @click="applyFormatting('alignRight')"
                        :class="[
                          'p-1.5 rounded transition-all',
                          editor?.isActive({ textAlign: 'right' })
                            ? 'text-primary bg-primary/10'
                            : 'hover:text-foreground hover:bg-muted/30',
                        ]"
                        title="Align Right"
                      >
                        <AlignRight class="w-5 h-5 stroke-[1.5]" />
                      </button>
                      <div class="relative">
                        <button
                          @click="applyFormatting('link')"
                          :class="[
                            'p-1.5 rounded transition-all',
                            editor?.isActive('link') || showLinkInput
                              ? 'text-primary bg-primary/10'
                              : 'hover:text-foreground hover:bg-muted/30',
                          ]"
                          title="Link"
                        >
                          <Link2 class="w-5 h-5 stroke-[1.5]" />
                        </button>

                        <!-- Custom Link Input Popup -->
                        <div
                          v-if="showLinkInput"
                          v-click-outside="() => (showLinkInput = false)"
                          class="absolute top-full left-0 mt-2 p-2 bg-card border border-border rounded-xl shadow-xl z-50 flex items-center gap-2 animate-in slide-in-from-top-2 duration-200 min-w-[240px]"
                        >
                          <input
                            v-model="linkInputUrl"
                            type="text"
                            placeholder="https://..."
                            class="flex-1 bg-muted/30 border border-border/50 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-primary/50 text-foreground"
                            @keydown.enter.prevent="confirmLink"
                            @keydown.esc="showLinkInput = false"
                            ref="linkInput"
                          />
                          <button
                            @click="confirmLink"
                            class="p-1.5 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
                            title="Confirm"
                          >
                            <Check class="w-4 h-4" />
                          </button>
                          <button
                            v-if="editor?.isActive('link')"
                            @click="removeLink"
                            class="p-1.5 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-colors"
                            title="Remove Link"
                          >
                            <Trash2 class="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <button
                      @click="saveNote"
                      class="px-5 py-2 sm:px-6 sm:py-2.5 bg-[#3A3F47] dark:bg-foreground dark:text-background text-white rounded-full text-[14px] font-medium hover:opacity-90 transition-opacity whitespace-nowrap"
                    >
                      {{ editingNote ? "Update Note" : "Save Note" }}
                    </button>
                  </div>

                  <!-- Content Area -->
                  <div
                    class="relative mt-3 flex-1 min-h-[350px] border border-border/80 rounded-[12px] p-5 bg-transparent group overflow-y-auto"
                  >
                    <editor-content
                      :editor="editor"
                      class="editor-content w-full h-full bg-transparent text-[16px] text-foreground/85 outline-none leading-relaxed"
                      @keydown.meta.k.prevent="toggleLinkInput"
                      @keydown.ctrl.k.prevent="toggleLinkInput"
                    />
                    <button
                      class="absolute bottom-4 right-4 p-2 rounded hover:bg-muted transition-colors text-muted-foreground/30 hover:text-muted-foreground/60 transition-colors cursor-pointer"
                      type="button"
                      title="Expand"
                    >
                      <Maximize2 class="w-5 h-5" />
                    </button>
                  </div>

                  <div class="flex items-center justify-end pt-2">
                    <button
                      @click="closeNoteEditor"
                      class="px-5 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted rounded-full transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Side Panel -->
          <transition name="slide-panel">
            <div
              v-if="showSidePanel"
              class="fixed inset-0 z-50"
              @click="closeSidePanel"
            >
              <div
                class="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
              ></div>
              <div
                class="absolute right-0 top-0 h-full w-full md:w-[534px] bg-card shadow-2xl overflow-y-auto"
                @click.stop
              >
                <div class="p-6">
                  <!-- Header -->
                  <div
                    class="flex items-center justify-between mb-6 pb-4 border-b border-border"
                  >
                    <h2
                      class="text-[18px] font-semibold text-foreground flex-1 pr-4"
                    >
                      {{ selectedStep?.title }}
                    </h2>
                    <div class="flex items-center space-x-2">
                      <button
                        class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
                        type="button"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            cx="4"
                            cy="10"
                            r="1.5"
                            class="fill-muted-foreground"
                          />
                          <circle
                            cx="10"
                            cy="10"
                            r="1.5"
                            class="fill-muted-foreground"
                          />
                          <circle
                            cx="16"
                            cy="10"
                            r="1.5"
                            class="fill-muted-foreground"
                          />
                        </svg>
                      </button>
                      <button
                        @click="closeSidePanel"
                        class="w-8 h-8 flex items-center justify-center rounded-full border transition-colors bg-muted/30 border-border"
                        type="button"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M15 5L5 15M5 5L15 15"
                            class="stroke-muted-foreground"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <!-- Status Row -->
                  <div class="flex items-center mb-4">
                    <div class="flex items-center space-x-3 w-[140px]">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1.63379 9.97998C2.34712 12.2733 4.26713 14.04 6.65379 14.5266"
                          class="stroke-muted-foreground"
                          stroke-width="1.2"
                          stroke-miterlimit="10"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M1.36621 7.32016C1.70621 3.9535 4.54621 1.3335 7.99954 1.3335C11.4529 1.3335 14.2929 3.96016 14.6329 7.32016"
                          class="stroke-muted-foreground"
                          stroke-width="1.2"
                          stroke-miterlimit="10"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M9.33984 14.5332C11.7198 14.0465 13.6332 12.2999 14.3598 10.0132"
                          class="stroke-muted-foreground"
                          stroke-width="1.2"
                          stroke-miterlimit="10"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>

                      <span class="text-[14px] text-muted-foreground"
                        >Status</span
                      >
                    </div>
                    <div
                      class="flex items-center space-x-2 px-3 py-1 rounded-full"
                      :class="
                        selectedStep?.completed
                          ? 'bg-green-500/10'
                          : 'bg-amber-500/10'
                      "
                    >
                      <span
                        class="w-2 h-2 rounded-full"
                        :class="
                          selectedStep?.completed
                            ? 'bg-green-500'
                            : 'bg-amber-500'
                        "
                      ></span>
                      <span
                        class="text-[13px] font-medium"
                        :class="
                          selectedStep?.completed
                            ? 'text-green-500'
                            : 'text-amber-500'
                        "
                      >
                        {{
                          selectedStep?.completed ? "Completed" : "In Progress"
                        }}
                      </span>
                    </div>
                  </div>

                  <!-- Estimated Time Row -->
                  <div class="flex items-center mb-6">
                    <div class="flex items-center space-x-3 w-[140px]">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.8337 8.83333C13.8337 12.0533 11.2203 14.6667 8.00033 14.6667C4.78033 14.6667 2.16699 12.0533 2.16699 8.83333C2.16699 5.61333 4.78033 3 8.00033 3C11.2203 3 13.8337 5.61333 13.8337 8.83333Z"
                          class="stroke-muted-foreground"
                          stroke-width="1.2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M8 5.3335V8.66683"
                          class="stroke-muted-foreground"
                          stroke-width="1.2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M6 1.3335H10"
                          class="stroke-muted-foreground"
                          stroke-width="1.2"
                          stroke-miterlimit="10"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>

                      <span class="text-[14px] text-muted-foreground">
                        Estimated time
                      </span>
                    </div>
                    <span class="text-[14px] text-muted-foreground/70">{{
                      formatHoursToHMS(selectedStep?.estimated_time)
                    }}</span>
                  </div>

                  <!-- Divider -->
                  <div class="border-t border-gray-200 mb-6"></div>

                  <!-- Summary Section -->
                  <div class="mb-6">
                    <h3
                      class="text-[14px] font-semibold text-muted-foreground mb-3"
                    >
                      Summary
                    </h3>
                    <div class="border border-border rounded-[8px] p-4">
                      <p class="text-[14px] text-foreground leading-relaxed">
                        {{
                          selectedStep?.description ||
                          "No description provided."
                        }}
                      </p>
                    </div>
                  </div>

                  <!-- Key Points Section -->
                  <div
                    class="mb-6"
                    v-if="
                      selectedStep?.key_points &&
                      selectedStep.key_points.length > 0
                    "
                  >
                    <h3
                      class="text-[14px] font-semibold text-muted-foreground mb-3"
                    >
                      Key points
                    </h3>
                    <div class="border border-border rounded-[8px] p-4">
                      <ul class="space-y-2">
                        <li
                          v-for="(point, index) in selectedStep.key_points"
                          :key="index"
                          class="flex items-start space-x-2 text-[14px] text-foreground"
                        >
                          <span class="text-foreground mt-0.5">•</span>
                          <span>{{ point }}</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <!-- Resource Links Section -->
                  <div class="mb-6">
                    <h3
                      class="text-[14px] font-semibold text-muted-foreground mb-3"
                    >
                      Resource Links
                    </h3>
                    <div
                      class="border border-border rounded-[8px] overflow-hidden"
                    >
                      <div
                        v-for="(resource, index) in getAllResources(
                          selectedStep,
                        )"
                        :key="index"
                        class="flex items-center px-4 py-3 hover:bg-muted/50 transition-colors"
                      >
                        <div class="w-[100px] text-[14px] text-foreground">
                          {{ getResourceLabel(resource, index) }}
                        </div>
                        <div class="w-10 flex items-center justify-center">
                          <FileText class="w-5 h-5 text-muted-foreground" />
                        </div>
                        <button
                          type="button"
                          @click="promptExternalLink(resource.url)"
                          class="flex-1 text-[14px] text-foreground hover:text-primary transition-colors truncate text-left"
                        >
                          {{ resource.title || "Resource link" }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="sticky bottom-0 bg-card pt-4 mt-6">
                    <div class="flex items-center justify-between gap-3">
                      <button
                        @click="navigateToPreviousSubTask"
                        :disabled="!hasPreviousSubTask"
                        class="flex items-center gap-2 px-3 py-2 text-[12px] font-medium text-foreground bg-card border border-border rounded-[8px] hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        type="button"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12.5 15L7.5 10L12.5 5"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                        Previous
                      </button>

                      <div class="flex items-center gap-2 flex-1">
                        <button
                          @click="onShareSubTask"
                          class="flex items-center justify-center gap-2 px-3 py-2 text-[12px] font-medium text-foreground bg-card border border-border rounded-[8px] hover:bg-muted transition-colors"
                          type="button"
                        >
                          Share
                        </button>

                        <button
                          @click="onMarkSubTaskComplete"
                          class="flex items-center justify-center gap-2 px-3 py-2 text-[12px] font-medium text-foreground bg-card border border-border rounded-[8px] hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1"
                          type="button"
                          :disabled="selectedStep?.completed"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M16.25 5L7.5 13.75L3.75 10"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                          Mark as Complete
                        </button>
                      </div>

                      <button
                        @click="navigateToNextSubTask"
                        :disabled="!hasNextSubTask"
                        class="flex items-center gap-2 px-4 py-2 text-[12px] font-medium text-white bg-black dark:bg-white/10 border border-transparent rounded-[8px] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        type="button"
                      >
                        Next
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7.5 15L12.5 10L7.5 5"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </transition>

          <!-- External link confirmation modal -->
          <ExternalLinkModal
            :isVisible="externalLinkModalVisible"
            :url="externalLinkUrl"
            :showUrl="true"
            @cancel="closeExternalLinkModal"
            @continue="confirmExternalLink"
          />

          <!-- Share Progress Modal -->
          <transition name="modal-fade">
            <div
              v-if="showShareModal"
              class="fixed inset-0 z-50 flex items-center justify-center"
              @click="closeShareModal"
            >
              <div class="absolute inset-0 bg-black bg-opacity-50"></div>
              <div
                class="relative bg-white rounded-[16px] w-full max-w-[540px] mx-4 shadow-xl"
                @click.stop
              >
                <!-- Modal Header -->
                <div
                  class="flex items-center justify-between p-6 border-b border-gray-200"
                >
                  <h2 class="text-[20px] font-semibold text-gray-900">
                    Share Progress
                  </h2>
                  <button
                    @click="closeShareModal"
                    class="text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 6L6 18M6 6L18 18"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                </div>

                <!-- Modal Body -->
                <div class="p-6">
                  <p class="text-[14px] text-gray-600 mb-4">
                    Share your progress with the community. We've drafted some
                    posts for you based on your path details.
                  </p>

                  <!-- Post Draft Section -->
                  <div class="mb-4">
                    <div class="flex items-center justify-between mb-2">
                      <label class="text-[13px] font-medium text-gray-700">
                        Post draft
                      </label>
                      <span class="text-[12px] text-gray-500">
                        {{ shareCharCount }} characters
                      </span>
                    </div>
                    <textarea
                      v-model="sharePostDraft"
                      rows="6"
                      class="w-full px-3 py-2 border border-gray-300 rounded-[8px] text-[13px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                      placeholder="Write your post here..."
                    ></textarea>
                  </div>

                  <!-- Goal Link -->
                  <div
                    class="flex items-center gap-3 bg-muted/50 rounded-[8px] p-3 mb-4"
                  >
                    <div
                      class="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8.33333 11.6667C8.69117 12.1089 9.13776 12.471 9.64509 12.7295C10.1524 12.988 10.7095 13.1368 11.2792 13.1663C11.8488 13.1959 12.4183 13.1054 12.9509 12.9005C13.4835 12.6957 13.9672 12.3813 14.3708 11.9775L16.8708 9.47751C17.6219 8.69626 18.0381 7.6502 18.0287 6.56396C18.0193 5.47773 17.5851 4.43916 16.8211 3.67116C16.057 2.90316 15.0203 2.46354 13.9341 2.44843C12.8478 2.43332 11.7992 2.84376 11.015 3.58584L9.79167 4.80084"
                          stroke="currentColor"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M11.6667 8.33333C11.3088 7.89112 10.8622 7.529 10.3549 7.27049C9.84759 7.01199 9.29049 6.86321 8.72084 6.83365C8.15119 6.80409 7.58168 6.89459 7.04909 7.09946C6.5165 7.30432 6.03278 7.61868 5.62917 8.0225L3.12917 10.5225C2.37808 11.3038 1.96192 12.3498 1.97134 13.4361C1.98076 14.5223 2.41492 15.5608 3.17893 16.3288C3.94294 17.0968 4.97966 17.5365 6.06589 17.5516C7.15213 17.5667 8.20078 17.1562 8.985 16.4142L10.2 15.1992"
                          stroke="currentColor"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-[12px] text-gray-500 mb-0.5">Goal Link</p>
                      <p class="text-[13px] text-gray-900 truncate">
                        {{ shareGoalUrl }}
                      </p>
                    </div>
                  </div>

                  <!-- Action Buttons -->
                  <div class="flex items-center gap-3">
                    <button
                      @click="copyPostText"
                      class="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-[8px] hover:bg-gray-50 dark:border-border dark:text-muted-foreground dark:hover:bg-muted transition-colors font-medium text-[14px]"
                      type="button"
                    >
                      Copy Post
                    </button>
                    <div class="flex items-center gap-2 ml-auto">
                      <a
                        :href="getFacebookShareUrl()"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center hover:opacity-90 transition-opacity"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 14.9912 3.65684 19.1283 8.4375 19.8785V12.8906H5.89844V10H8.4375V7.79688C8.4375 5.29063 9.93047 3.90625 12.2146 3.90625C13.3084 3.90625 14.4531 4.10156 14.4531 4.10156V6.5625H13.1922C11.95 6.5625 11.5625 7.3334 11.5625 8.125V10H14.3359L13.8926 12.8906H11.5625V19.8785C16.3432 19.1283 20 14.9912 20 10Z"
                            fill="white"
                          />
                        </svg>
                      </a>

                      <a
                        :href="getLinkedInShareUrl()"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="w-10 h-10 rounded-full bg-[#0A66C2] flex items-center justify-center hover:opacity-90 transition-opacity"
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M16.2 0H1.8C0.81 0 0 0.81 0 1.8V16.2C0 17.19 0.81 18 1.8 18H16.2C17.19 18 18 17.19 18 16.2V1.8C18 0.81 17.19 0 16.2 0ZM5.4 15.3H2.7V6.75H5.4V15.3ZM4.05 5.58C3.15 5.58 2.43 4.86 2.43 3.96C2.43 3.06 3.15 2.34 4.05 2.34C4.95 2.34 5.67 3.06 5.67 3.96C5.67 4.86 4.95 5.58 4.05 5.58ZM15.3 15.3H12.6V11.07C12.6 10.17 12.6 8.91 11.25 8.91C9.9 8.91 9.72 9.99 9.72 11.07V15.3H7.02V6.75H9.63V7.92H9.66C10.08 7.17 11.07 6.39 12.51 6.39C15.21 6.39 15.3 8.28 15.3 10.62V15.3Z"
                            fill="white"
                          />
                        </svg>
                      </a>

                      <a
                        :href="getWhatsAppShareUrl()"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center hover:opacity-90 transition-opacity"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.0016 0H9.99844C4.48625 0 0 4.48656 0 10C0 12.1928 0.705313 14.2194 1.90375 15.8613L0.6575 19.5781L4.50188 18.3506C6.08563 19.4147 7.97094 20.0003 10.0016 20.0003C15.5144 20.0003 20 15.5134 20 10C20 4.48656 15.5144 0 10.0016 0Z"
                            fill="white"
                          />
                          <path
                            d="M15.8195 14.1924C15.5801 14.9408 14.5892 15.5533 13.8042 15.7367C13.2714 15.8586 12.5789 15.9558 10.2945 14.9789C7.41575 13.7508 5.52919 10.7952 5.37825 10.5961C5.23263 10.3971 4.14575 8.95733 4.14575 7.46671C4.14575 5.97608 4.91356 5.2502 5.20231 4.95608C5.44169 4.71233 5.83294 4.59733 6.20575 4.59733C6.32544 4.59733 6.43263 4.60358 6.52825 4.60889C6.81731 4.61983 6.96231 4.63483 7.15575 5.11139C7.39513 5.70014 8.01044 7.19077 8.08419 7.33983C8.15888 7.48889 8.23263 7.68764 8.13169 7.88639C8.03606 8.09045 7.95169 8.17952 7.80075 8.35327C7.64981 8.52733 7.50575 8.65983 7.35481 8.84483C7.21513 8.99983 7.06013 9.16545 7.23294 9.45358C7.40575 9.73639 8.00481 10.7302 8.90419 11.5258C10.0429 12.5324 10.9923 12.8408 11.3167 12.9708C11.5561 13.0652 11.8389 13.0433 12.017 12.8555C12.2417 12.6152 12.5286 12.2058 12.8195 11.8021C13.0267 11.5108 13.2826 11.4774 13.5567 11.5717C13.8339 11.6608 15.317 12.3952 15.6167 12.5446C15.9164 12.6933 16.1151 12.7652 16.1898 12.8871C16.2636 13.0089 16.2636 13.5977 15.8195 14.1924Z"
                            fill="#25D366"
                          />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </transition>

          <!-- View Note Modal -->
          <transition name="modal-fade">
            <div
              v-if="showViewNoteModal"
              class="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50"
              @click="closeViewNoteModal"
            >
              <div
                class="bg-background border border-border rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col"
                @click.stop
              >
                <!-- Modal Header -->
                <div
                  class="flex items-center justify-between p-6 border-b border-border"
                >
                  <h3 class="text-xl font-semibold text-foreground">
                    {{ viewingNote?.title }}
                  </h3>
                  <button
                    @click="closeViewNoteModal"
                    class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 5L5 15M5 5L15 15"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                </div>

                <!-- Modal Body -->
                <div class="p-6 overflow-y-auto flex-1">
                  <div class="text-sm text-muted-foreground mb-4">
                    {{ formatNoteDate(viewingNote?.created_at) }}
                  </div>
                  <div
                    v-html="viewingNote?.content"
                    class="prose prose-sm max-w-none text-foreground"
                    style="word-break: break-word"
                  ></div>
                </div>

                <!-- Modal Footer -->
                <div
                  class="flex items-center justify-end gap-3 p-6 border-t border-border"
                >
                  <button
                    @click="closeViewNoteModal"
                    class="px-4 py-2 border border-border rounded-full hover:bg-muted transition-colors"
                  >
                    Close
                  </button>
                  <button
                    @click="
                      openNoteEditor(viewingNote);
                      closeViewNoteModal();
                    "
                    class="px-4 py-2 bg-primary text-primary-foreground rounded-full hover:opacity-90 transition-colors"
                  >
                    Edit Note
                  </button>
                </div>
              </div>
            </div>
          </transition>

          <!-- Delete Goal Modal -->
          <DeleteProjectModal
            :isOpen="deleteModalOpen"
            :projectId="projectId"
            :projectName="goal?.topic || ''"
            @close="closeDeleteModal"
            @confirm-delete="confirmDeleteProject"
          />
        </div>
      </div>
      <!-- Lock-in Mode Overlay -->
      <LockInModeComponent
        :isVisible="showLockInOverlay"
        :goalData="goal"
        :initialTaskId="activeTaskId"
        @close="showLockInOverlay = false"
      />

      <SubmitSubProjectModal
        :showModal="showSubmitModal"
        :project="projectToSubmit"
        :projectId="goal?.project_id"
        @close="showSubmitModal = false"
        @submitted="handleProjectSubmitted"
      />
    </div>
  </div>
</template>

<script>
import apiClient from "@/axios";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import UserAvatar from "@/components/auth/UserAvatar.vue";
import ExternalLinkModal from "@/components/modals/ExternalLinkModal.vue";
import DeleteProjectModal from "@/components/modals/DeleteProjectModal.vue";
import SubmitSubProjectModal from "../modals/SubmitSubProjectModal.vue";
import LearningGoalActionsBar from "./LearningGoalActionsBar.vue";
import expertBronzeBadge from "@/assets/achievements/bronze-medal.svg";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Check,
  Lock,
  PlayCircle,
  FileText,
  HelpCircle,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  NotebookTabs,
  RotateCw,
  EllipsisVertical,
  Trash2,
  Bold,
  Italic,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link2,
  Maximize2,
  BookOpen,
  Rocket,
  Award,
} from "lucide-vue-next";
import BackButton from "@/components/BackButton.vue";
import Loader from "../ui/Loader.vue";
import LockInModeComponent from "../lock-in-mode/LockInModeComponent.vue";
import { Editor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import LinkExtension from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";

export default {
  name: "LearningGoalDetailsComponent",
  components: {
    SubmitSubProjectModal,
    LockInModeComponent,
    SkeletonLoader,
    UserAvatar,
    ExternalLinkModal,
    LearningGoalActionsBar,
    Tabs,
    TabsList,
    TabsTrigger,
    Check,
    Lock,
    PlayCircle,
    FileText,
    HelpCircle,
    ChevronRight,
    ChevronDown,
    ChevronUp,
    NotebookTabs,
    RotateCw,
    EllipsisVertical,
    Trash2,
    BackButton,
    Loader,
    DeleteProjectModal,
    Bold,
    Italic,
    List,
    ListOrdered,
    AlignLeft,
    AlignCenter,
    AlignRight,
    // Link,
    Link2,
    Maximize2,
    EditorContent,
    BookOpen,
    Rocket,
    Award,
  },
  data() {
    return {
      editor: null,
      parentTaskId: null,
      goal: null,
      showLockInOverlay: false,
      showSubmitModal: false,
      projectToSubmit: null,
      activeTaskId: null,
      loading: true,
      showError: false,
      errorMessage: "",
      descriptionExpanded: false,
      defaultDescription: "View details for your selected learning goal.",
      tabs: ["Overview", "Modules", "Notes"],
      activeTab: "Overview",
      expandedSections: ["lessons"],

      expertBronzeBadge,
      loadingMentors: false,
      mentorSearchQuery: "",
      mentorSearchTimeout: null,
      mentorSkillsDropdownOpen: false,
      mentorSkillsOptions: [],
      selectedMentorSkillId: null,
      mentorSortDropdownOpen: false,
      mentorSortFilter: "newest",
      mentors: [],
      selectedMentorId: null,
      submittingMentor: false,
      expandedModules: [0], // Auto-expand first module

      showSidePanel: false,
      selectedParentTask: null,
      selectedSubTask: null,
      selectedModuleIndex: null,
      selectedSubTaskIndex: null,

      externalLinkModalVisible: false,
      externalLinkUrl: "",

      showShareModal: false,
      showSuccess: false,
      successMessage: "",

      // Edit Drawer State
      showEditDrawer: false,
      isGeneratingPlan: false,
      editingField: null,
      experienceLevels: ["Beginner", "Intermediate", "Advanced"],
      editForm: {
        topic: "",
        experience: "",
        timeCommitment: "",
        mentor: false,
        workspace_id: null,
        additional_information: "",
        learning_style: "video-first",
        resourceOrigin: "online",
        personal_resources: [],
      },
      learningStyles: [
        { value: "video-first", title: "Video-First" },
        { value: "reading-first", title: "Reading-First" },
        // { value: "interactive", title: "Interactive" },
        { value: "balanced", title: "Balanced (Mix)" },
      ],
      resourceFiles: [],
      resourceOptions: [
        {
          title: "Free",
          value: "online",
          description:
            "This option gives you access to regular online materials that relate to your learning goal only",
        },
        {
          title: "High-Quality",
          value: "verified",
          description:
            "This option gives you access to high-quality pre-reviewed resources. Only available to paid users",
        },
        {
          title: "Personal",
          value: "personal",
          description:
            "This option allows you upload as many materials as you want. Suitable for narrowed learnings.",
        },
      ],
      isPaidUser: false,
      workspaceSearchQuery: "",
      workspaceSearchTimeout: null,
      workspaceOption: "not_yet",
      workspaces: [],
      loadingWorkspaces: false,
      expandedWorkspaceId: null,
      selectedWorkspaceData: null,
      workspaceDetails: null,
      durationOptions: [
        { title: "Any", value: null },
        { title: "4 weeks", value: 4 },
        { title: "8 weeks", value: 8 },
        { title: "12 weeks", value: 12 },
      ],

      // Deletion State
      showActionsDropdown: false,
      deleteModalOpen: false,

      // Error State
      showTechnicalError: false,

      // Notes State
      notes: [],
      loadingNotes: false,
      notesSearchQuery: "",
      isCreatingNote: false,
      editingNote: null,
      noteForm: {
        title: "",
        content: "",
      },
      showViewNoteModal: false,
      pollingInterval: null,

      // Link Input state
      showLinkInput: false,
      linkInputUrl: "",
    };
  },
  computed: {
    projectId() {
      return this.$route.params.id;
    },
    capstoneProjects() {
      const tasks = Array.isArray(this.goal?.tasks) ? this.goal.tasks : [];
      return tasks
        .filter((t) => t && t.learning_task_type === "project")
        .map((project) => {
          let practiceTask = project.practice_task;
          if (typeof practiceTask === "string") {
            try {
              practiceTask = JSON.parse(practiceTask);
            } catch (e) {
              console.error("Failed to parse practice_task", e);
            }
          }
          return {
            ...project,
            practice_task: practiceTask,
          };
        });
    },
    learningModules() {
      const tasks = Array.isArray(this.goal?.tasks) ? this.goal.tasks : [];
      const modules = tasks.filter(
        (t) =>
          t &&
          (t.parent_task_id === null || t.parent_task_id === undefined) &&
          t.learning_task_type !== "project",
      );
      return modules;
    },

    selectedStep() {
      if (!this.selectedSubTask) return null;
      return {
        ...this.selectedSubTask,
        completed: this.isTaskCompleted(this.selectedSubTask),
        estimated_time: this.estimateTaskHours(this.selectedSubTask),
      };
    },

    selectedModuleSubTasks() {
      if (!this.selectedParentTask) return [];
      return this.getSubTasks(this.selectedParentTask);
    },

    hasPreviousSubTask() {
      return (
        typeof this.selectedSubTaskIndex === "number" &&
        this.selectedSubTaskIndex > 0
      );
    },

    hasNextSubTask() {
      if (typeof this.selectedSubTaskIndex !== "number") return false;
      return this.selectedSubTaskIndex < this.selectedModuleSubTasks.length - 1;
    },

    sharePostDraft() {
      const stepTitle = this.selectedStep?.title || "this module";
      const goalTopic = this.goal?.topic || "my learning goal";
      return `One step closer to my goal: ${goalTopic}!\n\nI learnt the following in this module:\n• ${stepTitle}\n\nLearn more about this goal here: ${this.shareGoalUrl}`;
    },

    shareCharCount() {
      return this.sharePostDraft.length;
    },

    shareGoalUrl() {
      return `${window.location.origin}/learning-goals/${this.projectId}`;
    },

    showMentorPicker() {
      return !this.loading && !!this.goal && this.goal.has_mentor === false;
    },
    canStartAssessment() {
      return (this.goal?.progress || 0) >= 100 && !!this.goal?.has_assessment;
    },
    canSeeCertificates() {
      const courseDone = (this.goal?.progress || 0) >= 100;
      return courseDone;
    },
    selectedMentorSkillLabel() {
      if (this.selectedMentorSkillId === null) return "Skills";
      const found = this.mentorSkillsOptions.find(
        (s) => s.id === this.selectedMentorSkillId,
      );
      return found ? found.name : "Skills";
    },
    mentorSortLabel() {
      const sortMap = {
        newest: "Latest",
        oldest: "Oldest",
      };
      return sortMap[this.mentorSortFilter] || "Latest";
    },
    getContextPlaceholder() {
      const map = {
        topic: "Describe it in your own words",
        experience: "Beginner, Intermediate...",
        timeCommitment: "Type your answer or select from the options above",
        mentor: "Search for a mentor by name or skills",
        workspace: "Search for a workspace by name or city",
        additional_information: "Add any additional context",
      };
      return map[this.editingField] || "Type here...";
    },
    selectedMentorDetails() {
      if (this.goal?.mentor) return this.goal.mentor;
      if (!this.selectedMentorId) return null;
      return (
        this.mentors.find((m) => m.talent_id === this.selectedMentorId) || null
      );
    },
    filteredNotes() {
      if (!this.notesSearchQuery.trim()) return this.notes;
      const query = this.notesSearchQuery.toLowerCase();
      return this.notes.filter(
        (note) =>
          note.title?.toLowerCase().includes(query) ||
          note.content?.toLowerCase().includes(query),
      );
    },
  },
  methods: {
    initEditor() {
      this.editor = new Editor({
        extensions: [
          StarterKit.configure({
            heading: {
              levels: [1],
            },
          }),
          LinkExtension.configure({
            openOnClick: false,
          }),
          Placeholder.configure({
            placeholder: "It is important for products...",
          }),
          TextAlign.configure({
            types: ["heading", "paragraph"],
          }),
        ],
        content: "<h1></h1>",
        onUpdate: ({ editor }) => {
          this.enforceFirstLineHeading(editor);
          this.handleAutoCapitalization(editor);
          this.noteForm.content = editor.getHTML();
        },
      });
    },
    enforceFirstLineHeading(editor) {
      if (!editor.isFocused) return;
      const { doc } = editor.state;
      const firstChild = doc.firstChild;

      if (
        !firstChild ||
        firstChild.type.name !== "heading" ||
        firstChild.attrs.level !== 1
      ) {
        editor.chain().setNodeSelection(0).setHeading({ level: 1 }).run();
      }
    },
    handleAutoCapitalization(editor) {
      if (!editor.isFocused) return;
      const { doc, selection } = editor.state;
      const { from } = selection;

      const firstNode = doc.firstChild;
      if (firstNode && firstNode.type.name === "heading" && from <= 10) {
        const text = firstNode.textContent;
        if (text.length === 1) {
          const char = text[0];
          if (char === char.toLowerCase() && char !== char.toUpperCase()) {
            editor
              .chain()
              .insertContentAt({ from: 1, to: 2 }, char.toUpperCase(), {
                updateSelection: false,
              })
              .run();
          }
        }
      }
    },
    onStartAssessment() {
      this.$router.push({
        name: "AIAssessment",
        params: { projectId: this.projectId },
      });
    },

    selectDuration(weeks) {
      if (weeks === null) {
        this.editForm.timeCommitment = "";
      } else {
        this.editForm.timeCommitment = `${weeks} weeks`;
      }
    },
    setEditingField(field) {
      this.editingField = field;
      this.$nextTick(() => {
        if (this.$refs.contextInput) {
          this.$refs.contextInput.focus();
        }
        if (field === "mentor" && this.editForm.mentor) {
          this.fetchMentors();
        } else if (field === "workspace" && this.workspaceOption === "yes") {
          this.fetchWorkspaces();
        } else if (field === "resourceOrigin") {
          // Any initialization for resource origin if needed
        }
      });
    },
    closeField() {
      this.editingField = null;
    },
    saveField() {
      this.editingField = null;
      // Trigger the plan generation
      this.submitEditGoal();
    },
    selectWorkspaceOption(option) {
      this.workspaceOption = option;
      if (option === "not_yet") {
        this.editForm.workspace_id = null;
        this.selectedWorkspaceData = null;
        this.workspaceSearchQuery = "";
        this.workspaces = [];
      } else {
        this.fetchWorkspaces();
      }
    },
    selectWorkspace(workspaceId) {
      if (this.editForm.workspace_id === workspaceId) {
        this.editForm.workspace_id = null;
        this.selectedWorkspaceData = null;
      } else {
        this.editForm.workspace_id = workspaceId;
        this.selectedWorkspaceData =
          this.workspaces.find((w) => w.id === workspaceId) || null;
      }
    },
    toggleWorkspaceExpansion(workspaceId) {
      if (this.expandedWorkspaceId === workspaceId) {
        this.expandedWorkspaceId = null;
      } else {
        this.expandedWorkspaceId = workspaceId;
      }
    },
    handleWorkspaceSearch() {
      clearTimeout(this.workspaceSearchTimeout);
      this.workspaceSearchTimeout = setTimeout(() => {
        this.fetchWorkspaces();
      }, 500);
    },
    async fetchWorkspaces() {
      try {
        this.loadingWorkspaces = true;
        const response = await apiClient.get("/web/workspace", {
          params: {
            search: this.workspaceSearchQuery,
            limit: 10,
          },
        });
        this.workspaces = response.data.data || [];
      } catch (error) {
        console.error("Error fetching workspaces:", error);
        this.workspaces = [];
      } finally {
        this.loadingWorkspaces = false;
      }
    },
    async handleResourceFileUpload(event) {
      const files = Array.from(event.target.files);
      if (files.length === 0) return;

      for (const file of files) {
        const fileId = Math.random().toString(36).substring(7);
        const fileObj = {
          id: fileId,
          name: file.name,
          status: "uploading",
        };

        this.resourceFiles.push(fileObj);

        try {
          const uploadData = new FormData();
          uploadData.append("file", file);

          const response = await apiClient.post(
            "/web/learning-goals/upload-resource",
            uploadData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            },
          );

          const currentFileIdx = this.resourceFiles.findIndex(
            (f) => f.id === fileId,
          );
          if (currentFileIdx === -1) continue;

          if (response.data && response.data.success) {
            this.editForm.resourceOrigin = "personal";
            this.resourceFiles[currentFileIdx].status = "success";
            this.editForm.personal_resources.push({
              id: fileId,
              filename: response.data.filename,
              file_key: response.data.file_key,
              url: response.data.url,
              full_text: response.data.full_text,
            });
            this.$toast.success("Success", `Processed ${file.name}`);
          } else {
            this.resourceFiles[currentFileIdx].status = "error";
            this.$toast.error(
              "Format Error",
              `Could not extract text from ${file.name}`,
            );
          }
        } catch (error) {
          console.error("Error uploading file:", error);
          const currentFileIdx = this.resourceFiles.findIndex(
            (f) => f.id === fileId,
          );
          if (currentFileIdx !== -1) {
            this.resourceFiles[currentFileIdx].status = "error";
          }
          this.$toast.error("Upload Failed", `Could not upload ${file.name}`);
        }
      }
    },
    removeResourceFile(index) {
      const fileId = this.resourceFiles[index].id;
      this.resourceFiles.splice(index, 1);
      this.editForm.personal_resources =
        this.editForm.personal_resources.filter((r) => r.id !== fileId);
    },
    closeEditDrawer() {
      this.showEditDrawer = false;
      this.editingField = null;
    },
    cancelEdit() {
      this.isGeneratingPlan = false;
      this.stopPolling();
    },
    async submitEditGoal() {
      try {
        this.isGeneratingPlan = true;

        // Map form data to API payload
        const payload = {
          topic: this.editForm.topic,
          target_skill_level:
            this.editForm.experience?.toLowerCase() || "intermediate",
          estimated_duration_weeks: this.parseWeeksFromTimeCommitment(
            this.editForm.timeCommitment,
          ),
          additional_information: this.editForm.additional_information || "",
          learning_style: this.editForm.learning_style || "video-first",
          mentor_id: this.editForm.mentor
            ? this.selectedMentorId || null
            : null,
          workspace_id: this.editForm.workspace_id || null,
          resourceOrigin: this.editForm.resourceOrigin || "online",
          personal_resources: this.editForm.personal_resources || [],
        };

        const response = await apiClient.put(
          `/web/learning-goals/${this.projectId}/edit`,
          payload,
        );

        console.log("Response:", response);

        // Refresh the goal data
        await this.fetchGoal();

        this.editingField = null;

        this.$toast.success(
          "Regenerating Plan",
          "Your learning plan is being updated in the background. You can stay here or come back later.",
        );
      } catch (error) {
        console.error("Error updating learning goal:", error);
        this.isGeneratingPlan = false;

        this.$toast.error(
          "Update Failed",
          error.response?.data?.message ||
            "Failed to update learning goal. Please try again.",
        );
      }
    },
    parseWeeksFromTimeCommitment(timeString) {
      if (!timeString || timeString.toLowerCase().includes("any")) return 4; // Default

      // Try to extract number from strings like "4 weeks", "2 months", etc.
      const match = timeString.match(/(\d+)/);
      if (match) {
        const num = parseInt(match[1]);
        // If it says "months", multiply by 4
        if (timeString.toLowerCase().includes("month")) {
          return num * 4;
        }
        return num;
      }
      return 4; // Default fallback
    },
    async fetchGoal(isPolling = false) {
      try {
        if (!isPolling) {
          this.loading = true;
        }
        const response = await apiClient.get(
          `/web/learning-goals/${this.projectId}`,
        );

        this.goal = response.data || null;

        // Auto-collapse completed modules
        this.autoCollapseCompletedModules();

        // Check if the goal is still processing
        const status = (this.goal?.status || "").toLowerCase();
        const treeStatus = (
          this.goal?.learning_tree?.status || ""
        ).toLowerCase();

        if (status === "processing" || treeStatus === "processing") {
          this.isGeneratingPlan = true;
          this.startPolling();
        } else {
          this.isGeneratingPlan = false;
          this.stopPolling();
        }

        if (this.goal && !this.showEditDrawer) {
          this.syncEditForm();
        }
        if (this.goal) {
          if (this.goal.workspace_id) {
            this.fetchWorkspaceDetails(this.goal.workspace_id);
          }
        }
      } catch (error) {
        console.error("Error fetching learning goal:", error);
        if (!isPolling) {
          this.handleError("Failed to load learning goal. Please try again.");
        }
      } finally {
        if (!isPolling) {
          this.loading = false;
        }
      }
    },
    startPolling() {
      if (this.pollingInterval) return;

      this.pollingInterval = setInterval(() => {
        this.fetchGoal(true);
      }, 5000); // Poll every 5 seconds
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    syncEditForm() {
      if (!this.goal) return;

      // Pre-fill edit form
      this.editForm.topic = this.goal.topic || "";

      // Map skill level
      const levelMap = {
        beginner: "Beginner",
        intermediate: "Intermediate",
        advanced: "Advanced",
      };
      const targetLevel =
        this.goal.target_skill_level || this.goal.metadata?.target_skill_level;
      this.editForm.experience = levelMap[targetLevel?.toLowerCase()];

      // Map duration
      const durationWeeks =
        this.goal.estimated_duration_weeks ||
        this.goal.metadata?.estimated_duration_weeks;
      if (durationWeeks) {
        this.editForm.timeCommitment = `${durationWeeks} weeks`;
      }

      // Map mentor
      this.editForm.mentor = !!this.goal.has_mentor;
      if (this.goal.mentor) {
        this.selectedMentorId =
          this.goal.mentor.talent_id ||
          this.goal.mentor.id ||
          (typeof this.goal.mentor === "number" ||
          typeof this.goal.mentor === "string"
            ? this.goal.mentor
            : null);
      }

      // Map Additional Information
      const meta = new Map(Object.entries(this.goal.metadata || {}));
      this.editForm.additional_information =
        meta.get("additional_information") || "";
      this.editForm.learning_style =
        meta.get("learning_style") || "video-first";
      this.editForm.resourceOrigin = meta.get("resourceOrigin") || "online";

      // Map Workspace
      if (this.goal.workspace_id) {
        this.editForm.workspace_id = this.goal.workspace_id;
        this.workspaceOption = "not_yet";
        // If we have workspace data in the goal, we can use it to pre-fill
        if (this.goal.workspace) {
          this.selectedWorkspaceData = this.goal.workspace;
        }
      } else {
        this.editForm.workspace_id = null;
        this.workspaceOption = "not_yet";
      }

      // Map Resource Data
      this.editForm.resourceOrigin = this.goal.resourceOrigin || "online";
      this.editForm.personal_resources = this.goal.personal_resources || [];
      // Populate resourceFiles for the UI display from existing personal_resources
      this.resourceFiles = (this.editForm.personal_resources || []).map(
        (r) => ({
          id: r.id || Math.random().toString(36).substring(7),
          name: r.filename || "Uploaded File",
          status: "success",
        }),
      );
    },

    normalizeStatus(status) {
      return (status || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_");
    },

    isTaskCompleted(task) {
      return this.normalizeStatus(task?.status) === "completed";
    },

    isModuleUnlocked(moduleIndex) {
      for (let i = 0; i < moduleIndex; i += 1) {
        if (!this.isTaskCompleted(this.learningModules[i])) return false;
      }
      return true;
    },

    normalizeTaskType(type) {
      const t = (type || "").toString().trim().toLowerCase();
      if (!t) return "";
      if (t === "assessment" || t === "assesment") return "assessment";
      if (t === "lesson") return "lesson";
      return t;
    },

    getSubTasks(module) {
      const list = Array.isArray(module?.sub_tasks) ? module.sub_tasks : [];
      // Filter out assessments from sub-modules list
      return list.filter(
        (t) => this.normalizeTaskType(t?.learning_task_type) !== "assessment",
      );
    },

    isSubTaskUnlocked(moduleIndex, subTaskIndex) {
      const module = this.learningModules[moduleIndex];
      if (!module) return false;
      if (
        !this.isModuleUnlocked(moduleIndex) &&
        !this.isTaskCompleted(module)
      ) {
        return false;
      }
      const subTasks = this.getSubTasks(module);
      for (let i = 0; i < subTaskIndex; i += 1) {
        if (!this.isTaskCompleted(subTasks[i])) return false;
      }
      return true;
    },

    isAssessmentTask(task) {
      return this.normalizeTaskType(task?.learning_task_type) === "assessment";
    },

    getSubTaskIcon(subTask) {
      return this.isAssessmentTask(subTask) ? "HelpCircle" : "NotebookTabs";
    },

    getSubTaskLinksCount(subTask) {
      return Array.isArray(subTask?.external_resources)
        ? subTask.external_resources.length
        : 0;
    },

    getSubTaskDurationLabel(subTask) {
      const estimated =
        subTask?.assessment_criteria?.estimated_hours ??
        subTask?.estimated_hours;
      return this.formatDuration(estimated);
    },

    truncateDescription(description) {
      if (!description) return "";
      const maxLength = 150;
      if (description.length <= maxLength) return description;
      return description.substring(0, maxLength).trim() + "...";
    },

    hasExpertAchievement(user) {
      if (!user) return false;
      if (user.has_expert_achievement === true) return true;
      const list = user.expert_achievements;
      return Array.isArray(list) && list.length > 0;
    },
    capitalizeFirstLetter(string) {
      if (!string) return "";
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    getRoleBadgeClass(role) {
      const roleLower = role?.toLowerCase() || "";
      if (roleLower === "talent") return "bg-primary";
      if (roleLower === "investor") return "bg-[#2ACB42]";
      if (roleLower === "manager") return "bg-[#FFBB0D]";
      return "bg-gray-500";
    },
    selectMentorSort(sort) {
      this.mentorSortFilter = sort;
      this.mentorSortDropdownOpen = false;
      this.fetchMentors();
    },
    handleMentorSearch() {
      clearTimeout(this.mentorSearchTimeout);
      this.mentorSearchTimeout = setTimeout(() => {
        this.fetchMentors();
      }, 500);
    },
    async fetchWorkspaceDetails(workspaceId) {
      if (!workspaceId) return;
      try {
        const response = await apiClient.get(`/web/workspace/${workspaceId}`);
        if (response.data) {
          this.workspaceDetails = response.data.data || response.data;
        }
      } catch (error) {
        console.error("Error fetching workspace details:", error);
        // Fallback or silent fail (template handles missing details)
      }
    },

    async fetchMentors() {
      try {
        this.loadingMentors = true;
        const sortParam =
          this.mentorSortFilter === "oldest" ? "oldest" : "newest";
        const params = new URLSearchParams({
          sort_by: sortParam,
          has_expert_achievement: "true",
        });

        if (this.mentorSearchQuery && this.mentorSearchQuery.trim()) {
          params.append("search", this.mentorSearchQuery.trim());
        }

        if (this.selectedMentorSkillId !== null) {
          params.append(
            "achievement_id",
            this.selectedMentorSkillId.toString(),
          );
        }

        const response = await apiClient.get(
          `/general/mentors?${params.toString()}`,
        );
        const payload = response.data || {};
        this.mentors = payload.data || [];
      } catch (error) {
        console.error("Error fetching mentors:", error);
        this.mentors = [];
      } finally {
        this.loadingMentors = false;
      }
    },
    async fetchMentorSkills() {
      try {
        const response = await apiClient.get("/web/achievements/experts");
        const list = response.data || response.data?.data || [];
        this.mentorSkillsOptions = Array.isArray(list) ? list : [];
      } catch (error) {
        console.error("Error fetching mentor skills:", error);
        this.mentorSkillsOptions = [];
      }
    },
    selectMentorSkill(option) {
      this.selectedMentorSkillId = option ? option.id : null;
      this.mentorSkillsDropdownOpen = false;
      this.fetchMentors();
    },
    selectMentor(mentorId) {
      this.selectedMentorId =
        this.selectedMentorId === mentorId ? null : mentorId;
    },
    clearSelectedMentor() {
      this.selectedMentorId = null;
    },
    async submitMentorSelection() {
      if (!this.selectedMentorId) return;
      try {
        this.submittingMentor = true;
        await apiClient.post(
          `/web/learning-goals/${this.projectId}/add-mentor`,
          {
            mentor_id: this.selectedMentorId,
          },
        );
        this.clearSelectedMentor();
        await this.fetchGoal();
      } catch (error) {
        console.error("Error submitting mentor selection:", error);
        this.handleError(
          error.response?.data?.message ||
            "Failed to submit mentor selection. Please try again.",
        );
      } finally {
        this.submittingMentor = false;
      }
    },
    viewMentorProfile(talentId) {
      const userId = this.mentors.find(
        (m) => m.talent_id === talentId,
      )?.user_id;
      if (!userId) return;
      this.$router.push(`/explore/profile/${userId}`);
    },
    bookMentorSession(userId) {
      console.log("Book session for user:", userId);
    },

    handleSubTaskClick(module, moduleIndex, subTask, subTaskIndex) {
      if (
        !this.isSubTaskUnlocked(moduleIndex, subTaskIndex) &&
        !this.isTaskCompleted(subTask)
      ) {
        this.$toast.warning(
          "Locked",
          "Complete previous topics to unlock this one.",
        );
        return;
      }
      this.selectedParentTask = module;
      this.selectedSubTask = subTask;
      this.selectedModuleIndex = moduleIndex;
      this.selectedSubTaskIndex = subTaskIndex;

      // Trigger Lock-in Mode Overlay
      this.activeTaskId = subTask.task_id || subTask.id;
      this.showLockInOverlay = true;
    },

    handleProjectClick(project) {
      this.projectToSubmit = project;
      this.showSubmitModal = true;
    },

    handleProjectSubmitted(payload) {
      // Find the project in goal.tasks and update its status
      const { project, submission } = payload;
      if (this.goal && this.goal.tasks) {
        const targetTask = this.goal.tasks.find(
          (t) => (t.task_id || t.id) === (project.task_id || project.id),
        );
        if (targetTask) {
          targetTask.project_submission = submission;
          targetTask.task_status = "Completed";

          // Re-evaluate goal status
          const totalTasks = this.goal.tasks.length;
          const completedTasks = this.goal.tasks.filter(
            (t) => t.task_status === "Completed",
          ).length;
          this.goal.progress = Math.round((completedTasks / totalTasks) * 100);

          if (completedTasks === totalTasks) {
            this.goal.status = "Completed";
          }
        }
      }
    },

    closeSidePanel() {
      this.showSidePanel = false;
      this.selectedParentTask = null;
      this.selectedSubTask = null;
      this.selectedModuleIndex = null;
      this.selectedSubTaskIndex = null;
    },

    navigateToPreviousSubTask() {
      if (!this.hasPreviousSubTask) return;
      const nextIndex = this.selectedSubTaskIndex - 1;
      const nextTask = this.selectedModuleSubTasks[nextIndex];
      if (!nextTask) return;
      this.selectedSubTaskIndex = nextIndex;
      this.selectedSubTask = nextTask;
    },

    navigateToNextSubTask() {
      if (!this.hasNextSubTask) return;
      const nextIndex = this.selectedSubTaskIndex + 1;
      const nextTask = this.selectedModuleSubTasks[nextIndex];
      if (!nextTask) return;
      this.selectedSubTaskIndex = nextIndex;
      this.selectedSubTask = nextTask;
    },

    onShareSubTask() {
      this.openShareModal();
    },

    async onMarkSubTaskComplete() {
      await this.markSubTaskAsComplete();
    },

    async markSubTaskAsComplete() {
      if (!this.selectedSubTask) return;

      this.showSuccess = false;
      this.successMessage = "";
      this.showError = false;
      this.errorMessage = "";

      try {
        const payload = {
          task_status: "Completed",
        };

        await apiClient.put(
          `/web/tasks/${this.selectedSubTask.task_id}?project_id=${this.projectId}`,
          payload,
        );

        this.$toast.success(
          "Task Completed",
          "You have successfully completed this task. Well done!",
        );

        // Update local state
        this.selectedSubTask.status = "Completed";
        if (this.selectedStep) {
          this.selectedStep.completed = true;
        }

        // Refresh goal data
        await this.fetchGoal();
      } catch (error) {
        console.error("Error marking task as complete:", error);
        let errorMsg =
          "An error occurred while updating the task. Please try again.";

        if (error.response) {
          if (error.response.status === 403) {
            errorMsg =
              "You are not allowed to mark this task as completed. Only the task creator can complete this task.";
          } else if (error.response.status === 401) {
            errorMsg =
              "You are not authorized to perform this action. Please log in again.";
          } else if (error.response.status === 404) {
            errorMsg = "Task not found. It may have been deleted.";
          } else if (error.response.status >= 500) {
            errorMsg = "Server error occurred. Please try again later.";
          }
        }

        this.$toast.error("Error", errorMsg);
      }
    },

    openShareModal() {
      this.showShareModal = true;
    },

    closeShareModal() {
      this.showShareModal = false;
    },

    async copyPostText() {
      try {
        await navigator.clipboard.writeText(this.sharePostDraft);
        this.$toast.success("Copied", "Post copied to clipboard!");
      } catch (error) {
        this.$toast.error(
          "Error",
          "Failed to copy to clipboard. Please try again.",
        );
      }
    },

    getFacebookShareUrl() {
      const url = encodeURIComponent(this.shareGoalUrl);
      const quote = encodeURIComponent(this.sharePostDraft);
      return `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`;
    },

    getLinkedInShareUrl() {
      const url = encodeURIComponent(this.shareGoalUrl);
      return `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    },

    getWhatsAppShareUrl() {
      const text = encodeURIComponent(
        `${this.sharePostDraft}\n\n${this.shareGoalUrl}`,
      );
      return `https://wa.me/?text=${text}`;
    },

    getAllResources(task) {
      return task?.external_resources || [];
    },

    determineResourceType(resource) {
      const url = (resource.url || resource.link || "").toLowerCase();
      const title = (resource.title || resource.name || "").toLowerCase();
      if (resource.type) return resource.type;
      if (
        url.includes("youtube") ||
        url.includes("youtu.be") ||
        title.includes("video")
      )
        return "video";
      if (title.includes("read") || url.endsWith(".pdf")) return "document";
      return "link";
    },

    getResourceLabel(resource, index) {
      const type = resource.type || this.determineResourceType(resource);
      const number = index + 1;
      if (type === "video") return `Watch ${number}`;
      if (type === "document") return `Read ${number}`;
      if (type === "playlist") return `Playlist ${number}`;
      return `Link ${number}`;
    },

    estimateTaskHours(task) {
      const resources = Array.isArray(task?.external_resources)
        ? task.external_resources
        : [];
      return resources.reduce(
        (sum, r) => sum + (Number(r?.estimated_hours) || 0),
        0,
      );
    },

    formatHoursToHMS(hours) {
      const num = Number(hours);
      if (!isFinite(num) || num < 0) return "n/a";
      const totalSeconds = Math.round(num * 3600);
      const h = Math.floor(totalSeconds / 3600);
      const m = Math.floor((totalSeconds % 3600) / 60);
      const s = totalSeconds % 60;
      const parts = [];
      if (h > 0) parts.push(`${h}h`);
      if (m > 0) parts.push(`${m}m`);
      if (s > 0 || parts.length === 0) parts.push(`${s}s`);
      return parts.join(" ");
    },

    promptExternalLink(url) {
      if (!url) return;
      this.externalLinkUrl = url;
      this.externalLinkModalVisible = true;
    },

    closeExternalLinkModal() {
      this.externalLinkModalVisible = false;
      this.externalLinkUrl = "";
    },

    confirmExternalLink() {
      const url = this.externalLinkUrl;
      this.closeExternalLinkModal();
      if (!url) return;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    isTaskLocked(index) {
      if (index === 0) return false;
      const previousTask = this.parentTasks[index - 1];
      return !this.hasCompletionIndicator(previousTask?.status);
    },

    handleTaskClick(task) {
      if (task.is_locked) {
        console.log("Task is locked. Complete previous tasks first.");
        return;
      }
      this.$router.push({
        name: "LearningGoalTaskDetails",
        params: {
          id: this.projectId, // Using 'id' to match route parameter
          taskId: task.task_id,
        },
      });
    },

    navigateToProjectView() {
      this.$router.push({
        name: "ProjectDetails",
        params: { projectId: this.projectId },
      });
    },
    onBookMentor(response) {
      if (response && response.message) {
        this.$toast.success("Session Booked", response.message);
      } else {
        // If called from the redesign card directly, trigger the modal on the actions bar
        this.$refs.actionsBar?.openBookingModal();
      }
    },
    onEditGoal() {
      this.syncEditForm();
      this.showEditDrawer = true;
    },
    toggleDescription() {
      this.descriptionExpanded = !this.descriptionExpanded;
    },
    getLearningStyleTitle(value) {
      if (!value) return "Video-First";
      const style = this.learningStyles.find((s) => s.value === value);
      return style ? style.title : value;
    },
    onSeeCertificates() {
      this.$router.push({
        name: "Certificates",
        params: { projectId: this.projectId },
      });
    },
    onStartAIAssessment() {
      this.$router.push({
        name: "AIAssessment",
        params: { projectId: this.projectId },
      });
    },
    getStatusColor(status) {
      switch (status?.toLowerCase()) {
        case "completed":
          return {
            text: "text-primary",
            bg: "bg-primary/10",
            border: "border-primary/20",
          };
        case "processing":
          return { text: "#6366f1", bg: "#e0e7ff", border: "#818cf8" };
        case "in_progress":
        case "active":
          return { text: "#F59E0B", bg: "#FEF3C7", border: "#F59E0B" };
        default:
          return { text: "#EF4444", bg: "#FEF2F2", border: "#EF4444" };
      }
    },

    getStatusText(status) {
      switch (status?.toLowerCase()) {
        case "completed":
          return "Completed";
        case "processing":
          return "Processing";
        case "in_progress":
        case "active":
          return "In Progress";
        default:
          return "Not Started";
      }
    },

    hasCompletionIndicator(status) {
      return status?.toLowerCase() === "completed";
    },

    isProcessing(status) {
      return status?.toLowerCase() === "processing";
    },

    toggleModule(index, module) {
      if (!this.isTaskCompleted(module) && !this.isModuleUnlocked(index)) {
        this.$toast.warning(
          "Locked",
          "Complete previous modules to unlock this one.",
        );
        return;
      }
      if (this.expandedModules.includes(index)) {
        this.expandedModules = this.expandedModules.filter((i) => i !== index);
      } else {
        this.expandedModules.push(index);
      }
    },

    toggleSection(sectionName) {
      if (
        sectionName === "projects" &&
        this.getLessonsStatus() !== "Completed"
      ) {
        this.$toast?.error(
          "Locked",
          "Complete all lessons to unlock projects.",
        );
        return;
      }
      if (
        sectionName === "assessment" &&
        (this.getLessonsStatus() !== "Completed" ||
          this.getProjectsStatus() !== "Completed")
      ) {
        this.$toast?.error(
          "Locked",
          "Complete lessons and projects to unlock assessment.",
        );
        return;
      }

      if (this.expandedSections.includes(sectionName)) {
        this.expandedSections = this.expandedSections.filter(
          (s) => s !== sectionName,
        );
      } else {
        this.expandedSections.push(sectionName);
      }
    },

    getProjectsStatus() {
      if (!this.capstoneProjects || !this.capstoneProjects.length)
        return "Completed";
      const total = this.capstoneProjects.length;
      const completed = this.capstoneProjects.filter((p) =>
        this.isTaskCompleted(p),
      ).length;
      if (completed === total) return "Completed";
      if (completed > 0) return "In progress";
      return "Not started";
    },

    continueLearning() {
      if (!this.learningModules || !this.learningModules.length) return;

      let activeModuleIndex = this.learningModules.findIndex(
        (m) => !this.isTaskCompleted(m),
      );
      if (activeModuleIndex === -1) {
        activeModuleIndex = 0;
      }

      const module = this.learningModules[activeModuleIndex];
      if (!module) return;

      const subTasks = this.getSubTasks(module);
      let activeSubTaskIndex = subTasks.findIndex(
        (st) => !this.isTaskCompleted(st),
      );
      if (activeSubTaskIndex === -1) {
        activeSubTaskIndex = 0;
      }

      const subTask = subTasks[activeSubTaskIndex];
      if (subTask) {
        this.handleSubTaskClick(
          module,
          activeModuleIndex,
          subTask,
          activeSubTaskIndex,
        );
      }
    },

    getLessonsStatus() {
      if (!this.learningModules || !this.learningModules.length)
        return "Not started";
      const total = this.learningModules.length;
      const completed = this.learningModules.filter((m) =>
        this.isTaskCompleted(m),
      ).length;
      if (completed === total) return "Completed";
      if (completed > 0) return "In progress";

      for (const m of this.learningModules) {
        const subTasks = this.getSubTasks(m);
        if (subTasks.some((st) => this.isTaskCompleted(st))) {
          return "In progress";
        }
      }
      return "Not started";
    },

    getAssessmentStatus() {
      if (!this.goal?.has_assessment) return "Not started";

      const progress = this.goal.ai_assessment_progress?.progress || 0;

      if (
        this.goal.ai_assessment_progress?.status === "completed" ||
        progress >= 100 ||
        this.goal.status === "Completed"
      ) {
        return "Completed";
      }
      if (
        this.goal.ai_assessment_progress?.status === "in_progress" ||
        progress > 0
      ) {
        return "In progress";
      }
      return "Not started";
    },

    formatDuration(hours) {
      if (!hours) return "0m";
      const h = Math.floor(hours);
      const m = Math.round((hours - h) * 60);
      if (h > 0 && m > 0) return `${h}h ${m}m`;
      if (h > 0) return `${h}h`;
      return `${m}m`;
    },

    getLinksCount(topic) {
      if (topic.links_count !== undefined) return topic.links_count;
      let count = 0;
      if (topic.resources) {
        if (topic.resources.videos) count += topic.resources.videos.length || 0;
        if (topic.resources.playlists)
          count += topic.resources.playlists.length || 0;
        if (topic.resources.papers) count += topic.resources.papers.length || 0;
      }
      return count;
    },

    getTopicIcon(topic) {
      console.log(topic);
      // if (topic.assessment) return "HelpCircle";
      // if (topic.resources?.videos?.length > 0) return "PlayCircle";
      return "NotebookTabs"; // Default for generic resources or reading
    },

    handleError(message) {
      this.showError = true;
      this.errorMessage = message;
      setTimeout(() => {
        this.showError = false;
        this.errorMessage = "";
      }, 5000);
    },

    autoCollapseCompletedModules() {
      if (!this.learningModules || !this.learningModules.length) return;

      // Filter out completed modules from expandedModules
      const uncompletedExpanded = this.expandedModules.filter((index) => {
        const module = this.learningModules[index];
        return module && !this.isTaskCompleted(module);
      });

      // Update expandedModules
      this.expandedModules = uncompletedExpanded;

      // Ensure at least the first uncompleted module is expanded if none are
      if (this.expandedModules.length === 0) {
        const firstUncompletedIndex = this.learningModules.findIndex(
          (m) => !this.isTaskCompleted(m),
        );
        if (firstUncompletedIndex !== -1) {
          this.expandedModules = [firstUncompletedIndex];
        }
      }
    },

    toggleActionsDropdown() {
      this.showActionsDropdown = !this.showActionsDropdown;
    },

    closeActionsDropdown() {
      this.showActionsDropdown = false;
    },

    openDeleteModal() {
      this.deleteModalOpen = true;
      this.showActionsDropdown = false;
    },

    closeDeleteModal() {
      this.deleteModalOpen = false;
    },

    async confirmDeleteProject() {
      if (this.projectId) {
        await this.deleteLearningGoal(this.projectId);
      }
    },

    async deleteLearningGoal(id) {
      try {
        await apiClient.delete(`/web/learning-goals/${id}`);
        this.$toast.success("Success", "Learning goal deleted successfully");
        this.closeDeleteModal();
        this.$router.push({ name: "LearningGoals" });
      } catch (error) {
        console.error("Error deleting learning goal:", error);
        this.$toast.error(
          "Delete Failed",
          error.response?.data?.message ||
            "Failed to delete learning goal. Please try again.",
        );
      }
    },

    async fetchNotes() {
      if (!this.projectId) return;
      this.loadingNotes = true;
      try {
        const response = await apiClient.get(
          `/web/notes/${this.projectId}/all`,
          {
            params: {
              page: 1,
              page_size: 50,
              pinned_first: true,
            },
          },
        );
        this.notes = response.data.data?.notes || [];
      } catch (error) {
        console.error("Failed to fetch notes:", error);
        this.notes = [];
      } finally {
        this.loadingNotes = false;
      }
    },

    openNoteEditor(note = null) {
      this.isCreatingNote = true;
      if (note) {
        this.editingNote = note;
        this.noteForm.title = note.title || "";
        this.noteForm.content = note.content || "";
        this.$nextTick(() => {
          if (this.editor) {
            // Restore with H1 for consistent Apple experience
            this.editor.commands.setContent(
              `<h1>${note.title || ""}</h1>${note.content || ""}`,
            );
            this.editor.commands.focus();
          }
        });
      } else {
        this.editingNote = null;
        this.noteForm.title = "";
        this.noteForm.content = "";
        this.$nextTick(() => {
          if (this.editor) {
            this.editor.commands.setContent("<h1></h1>");
            this.editor.commands.focus();
          }
        });
      }
      this.noteMenuOpen = null;
    },

    closeNoteEditor() {
      this.isCreatingNote = false;
      this.editingNote = null;
      this.noteForm.title = "";
      this.noteForm.content = "";
    },

    async saveNote() {
      if (!this.editor || this.editor.isEmpty) return;

      const html = this.editor.getHTML();
      // Extract title from H1
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      const h1 = tempDiv.querySelector("h1");
      const title = h1 ? h1.innerText : "Untitled Note";

      // Content is everything except the first H1
      if (h1) h1.remove();
      const content = tempDiv.innerHTML;

      try {
        if (this.editingNote) {
          await apiClient.put(
            `/web/notes/${this.projectId}/note/${this.editingNote.id}`,
            {
              title: title || "Untitled Note",
              content: content.trim(),
              is_pinned: this.editingNote.is_pinned || false,
            },
          );
          this.$toast.success("Success", "Note updated successfully");
        } else {
          await apiClient.post(`/web/notes/${this.projectId}`, {
            title: title || "Untitled Note",
            content: content.trim(),
            is_pinned: false,
          });
          this.$toast.success("Success", "Note created successfully");
        }
        await this.fetchNotes();
        this.closeNoteEditor();
      } catch (error) {
        console.error("Failed to save note:", error);
        this.$toast.error(
          "Error",
          `Failed to ${this.editingNote ? "update" : "create"} note`,
        );
      }
    },

    toggleNoteMenu(noteId) {
      this.noteMenuOpen = this.noteMenuOpen === noteId ? null : noteId;
    },

    closeNoteMenu() {
      this.noteMenuOpen = null;
    },

    async deleteNote(note) {
      if (!confirm(`Are you sure you want to delete "${note.title}"?`)) return;

      try {
        await apiClient.delete(`/web/notes/${this.projectId}/note/${note.id}`);
        this.$toast.success("Success", "Note deleted successfully");
        await this.fetchNotes();
      } catch (error) {
        console.error("Failed to delete note:", error);
        this.$toast.error("Error", "Failed to delete note");
      } finally {
        this.noteMenuOpen = null;
      }
    },

    formatNoteDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);

      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[date.getMonth()];
      const day = date.getDate();

      // Add ordinal suffix
      const getOrdinal = (n) => {
        const s = ["th", "st", "nd", "rd"];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
      };

      const year = date.getFullYear();
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12; // 0 should be 12
      const minutesStr = minutes < 10 ? "0" + minutes : minutes;

      return `${month} ${getOrdinal(
        day,
      )}, ${year} at ${hours}:${minutesStr}${ampm}`;
    },

    truncateContent(content) {
      if (!content) return "No content";
      const strippedContent = content
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .trim();
      if (strippedContent.length <= 30) return strippedContent;
      return strippedContent.substring(0, 30) + "...";
    },

    openViewNoteModal(note) {
      this.viewingNote = note;
      this.showViewNoteModal = true;
      this.noteMenuOpen = null;
    },

    closeViewNoteModal() {
      this.showViewNoteModal = false;
      this.viewingNote = null;
    },

    toggleLinkInput() {
      this.showLinkInput = !this.showLinkInput;
      if (this.showLinkInput) {
        // Get current link if any
        this.linkInputUrl = this.editor.getAttributes("link").href || "";
        this.$nextTick(() => {
          this.$refs.linkInput?.focus();
          this.$refs.linkInput?.select();
        });
      }
    },
    removeLink() {
      this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
      this.showLinkInput = false;
      this.linkInputUrl = "";
    },
    confirmLink() {
      if (this.linkInputUrl) {
        let url = this.linkInputUrl;
        if (!url.startsWith("http")) {
          url = "https://" + url;
        }
        this.editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .setLink({ href: url })
          .run();
      } else {
        this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
      }
      this.showLinkInput = false;
      this.linkInputUrl = "";
    },
    applyFormatting(format) {
      if (!this.editor) return;

      const chain = this.editor.chain().focus();

      switch (format) {
        case "bold":
          chain.toggleBold().run();
          break;
        case "italic":
          chain.toggleItalic().run();
          break;
        case "bulletList":
          chain.toggleBulletList().run();
          break;
        case "numberedList":
          chain.toggleOrderedList().run();
          break;
        case "alignLeft":
          chain.setTextAlign("left").run();
          break;
        case "alignCenter":
          chain.setTextAlign("center").run();
          break;
        case "alignRight":
          chain.setTextAlign("right").run();
          break;
        case "link": {
          this.toggleLinkInput();
          break;
        }
      }
    },
  },

  created() {
    this.fetchGoal();
    this.fetchMentorSkills();
    this.fetchMentors();
    this.fetchNotes();

    if (this.$route.query.edit === "true") {
      this.showEditDrawer = true;
    }
  },
  mounted() {
    this.initEditor();
  },
  beforeUnmount() {
    if (this.editor) {
      this.editor.destroy();
    }
    this.stopPolling();
  },

  directives: {
    "click-outside": {
      beforeMount(el, binding) {
        el.clickOutsideEvent = function (event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.addEventListener("click", el.clickOutsideEvent);
      },
      unmounted(el) {
        document.removeEventListener("click", el.clickOutsideEvent);
      },
    },
  },
};
</script>

<style scoped>
/* Tiptap Editor Styles */
:deep(.tiptap) {
  height: 100%;
  min-height: 300px;
  outline: none;
}

:deep(.tiptap h1) {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 1rem;
  color: hsl(var(--foreground));
}

:deep(.tiptap p) {
  margin-bottom: 0.75rem;
  line-height: 1.6;
}

:deep(.tiptap ul) {
  list-style-type: disc;
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

:deep(.tiptap a) {
  color: #3b82f6;
  text-decoration: underline;
  cursor: pointer;
}

:deep(.tiptap .is-editor-empty:first-child::before) {
  content: "Untitled Note";
  float: left;
  color: #adb5bd;
  pointer-events: none;
  height: 0;
}

:deep(.tiptap p.is-editor-empty:first-child::before) {
  content: "It is important for products...";
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-panel-enter-active,
.slide-panel-leave-active {
  transition: opacity 0.3s ease;
}

.slide-panel-enter-active .absolute.right-0,
.slide-panel-leave-active .absolute.right-0 {
  transition: transform 0.3s ease;
}

.slide-panel-enter-from,
.slide-panel-leave-to {
  opacity: 0;
}

.slide-panel-enter-from .absolute.right-0 {
  transform: translateX(100%);
}

.slide-panel-leave-to .absolute.right-0 {
  transform: translateX(100%);
}

.slide-panel-enter-to .absolute.right-0,
.slide-panel-leave-from .absolute.right-0 {
  transform: translateX(0);
}

/* Modal Fade Animation */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-to,
.modal-fade-leave-from {
  opacity: 1;
}

/* Drawer Slide Animation */
.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: opacity 0.4s ease;
}

.drawer-slide-enter-active .relative,
.drawer-slide-leave-active .relative {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.drawer-slide-enter-from,
.drawer-slide-leave-to {
  opacity: 0;
}

.drawer-slide-enter-from .relative {
  transform: translateX(-100%);
}

.drawer-slide-leave-to .relative {
  transform: translateX(-100%);
}

.drawer-slide-enter-to .relative,
.drawer-slide-leave-from .relative {
  transform: translateX(0);
}

/* Expand Accordion Animation */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease-in-out;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
